/**
 * Browse view — Live TV, Movies, Series.
 *
 * Series get a 2-level drill-down:
 *   Category (group-title) → Series tile (one per series_name) → Season → Episode
 *
 * All data is client-side (parsed M3U). Posters / icons come from each
 * channel's `tvg-logo` attribute.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback, startTransition, useDeferredValue, memo } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import { getCachedSeriesEpisodes, cacheSeriesEpisodes, getCachedVodCategories, cacheVodCategories, getCachedVodStreams, cacheVodStreams } from "../lib/playlistCache";
import { getChannelsByGroup, getSeriesTiles, getMovieTiles } from "../lib/cacheBackend";
import { groupChannels } from "../lib/m3uParser";
import { currentProgramme } from "../lib/epgParser";
import MovieDetail from "../components/MovieDetail";
import { getFavorites, isFavorited, toggleFavorite, isFavoritable, favoritesCount } from "../lib/favorites";
import {
  getCue,
  isInCue,
  toggleCue as toggleMovieCue,
  removeFromCue,
  cueCount as movieCueCount,
  getIntermissionMinutes,
} from "../lib/movieCue";
import {
  hasNewEpisodes,
  markSeriesSeen,
  seedSeriesSeenAtIfMissing,
} from "../lib/seriesSeen";
import { getHistory, getContinueWatching, removeFromHistory, removeShowFromHistory } from "../lib/watchHistory";
import { isWatched, markWatched, markManyWatched, unmarkWatched, unmarkManyWatched, subscribe as subscribeWatched } from "../lib/watchedItems";
import useLongPress from "../lib/useLongPress";
import TileContextMenu from "../components/TileContextMenu";
import { isKidsActive, isAdultText, filterForKids } from "../lib/profiles";
import { fetchSeriesInfo, fetchXtreamSeriesEpisodes, fetchXtreamVodCategories, fetchXtreamVodStreams } from "../lib/xtreamApi";
import { fetchB2cSeriesEpisodes } from "../lib/oneStreamApi";
import { fetchTmdbTv, fetchTmdbTvByName } from "../lib/playerApi";
import { isAutoPlayNextEpisodeEnabled } from "../lib/autoPlayPref";
import { isTvLayout } from "../lib/tvDetect";
import { getHiddenCategories } from "../lib/hiddenCategories";
import { PLAYER } from "../lib/testIds";
import {
  ArrowLeft,
  Play,
  Loader2,
  Tv,
  Star,
  Heart,
  ListVideo,
  Popcorn,
  ChevronLeft,
  ArrowDownAZ,
  ArrowUpAZ,
  ListOrdered,
  Calendar,
  Clock,
  History,
  Menu as MenuIcon,
  X as XIcon,
  Search,
  Check,
  Sparkles,
  EyeOff,
  Eye,
  Trash2,
} from "lucide-react";
import PosterImage from "../components/PosterImage";
import { getTileSize, gridClassesFor } from "../lib/tileSize";
import VirtualTileGrid from "../components/VirtualTileGrid";

// v1.3.72 — switch to a virtualized grid only when the rendered
// list is large enough for the css-grid render to start dropping
// frames. Below this, the old static grid scrolls smoothly and we
// keep the existing layout intact.
// v1.4.60 — Restored to 200 after the definitive `VirtualTileGrid`
// fix in the same version. See VirtualTileGrid.jsx for the full
// multi-layered clamp that stops the "growing tile" bug from
// coming back.
const VIRTUAL_THRESHOLD = 200;

const TYPE_LABELS = {
  live_tv: "Live TV",
  movie: "Movies",
  series: "Series",
  catchup: "Catch Up",
};

const FAV_GROUP = "__favorites__";
const HISTORY_GROUP = "__continue_watching__";
// v1.0.9 — Movie Cue pseudo-group (Movies tab only). Content queued
// here plays back-to-back with a cinema intermission between titles.
const CUE_GROUP = "__movie_cue__";
// "All movies" / "All series" pseudo-group for the new Browse layout —
// Continue Watching is nested inside this view rather than being its own
// sidebar entry.
const ALL_GROUP = "__all__";

// Content types that get a "Continue watching" sidebar entry.
const CONTINUE_WATCHING_TYPES = new Set(["movie", "series"]);

const SORT_OPTIONS = {
  live_tv: [
    { v: "m3u", label: "Provider order", icon: ListOrdered },
    { v: "az", label: "A → Z", icon: ArrowDownAZ },
    { v: "za", label: "Z → A", icon: ArrowUpAZ },
  ],
  movie: [
    { v: "m3u", label: "Provider order", icon: ListOrdered },
    { v: "az", label: "A → Z", icon: ArrowDownAZ },
    { v: "za", label: "Z → A", icon: ArrowUpAZ },
    { v: "year_desc", label: "Newest first", icon: Calendar },
    { v: "added_desc", label: "Recently added", icon: Clock },
  ],
  series: [
    { v: "m3u", label: "Provider order", icon: ListOrdered },
    { v: "az", label: "A → Z", icon: ArrowDownAZ },
    { v: "za", label: "Z → A", icon: ArrowUpAZ },
    { v: "year_desc", label: "Newest first", icon: Calendar },
    { v: "ep_added_desc", label: "Recently episodes added", icon: Clock },
  ],
};

// Natural numeric comparator — used so "Movie 9" sorts before "Movie 10"
// and episode names like "E99" sort before "E155" (was the v1.3.32 bug).
const NATURAL_OPTS = { numeric: true, sensitivity: "base" };
const natCmp = (a, b) => (a || "").localeCompare(b || "", undefined, NATURAL_OPTS);

function applySort(list, sort) {
  if (sort === "az") return [...list].sort((a, b) => natCmp(a.name, b.name));
  if (sort === "za") return [...list].sort((a, b) => natCmp(b.name, a.name));
  if (sort === "year_desc")
    return [...list].sort((a, b) => (b.year || 0) - (a.year || 0));
  if (sort === "added_desc")
    return [...list].sort((a, b) => (b.added_ts || 0) - (a.added_ts || 0));
  return list; // m3u (provider) order = already sorted by `order` from the parser
}

function applySortSeries(list, sort) {
  if (sort === "az")
    return [...list].sort((a, b) => natCmp(a.series_name, b.series_name));
  if (sort === "za")
    return [...list].sort((a, b) => natCmp(b.series_name, a.series_name));
  if (sort === "year_desc")
    return [...list].sort((a, b) => (b.year || 0) - (a.year || 0));
  if (sort === "ep_added_desc")
    return [...list].sort(
      (a, b) => (b.last_episode_added_ts || 0) - (a.last_episode_added_ts || 0)
    );
  return list;
}

import LiveTv from "./LiveTv";

// Top-level wrapper: route live_tv to the new 3-pane layout, everything
// else through the original grid-based Browse implementation. Splitting
// avoids the rules-of-hooks early-return problem inside the big Browse
// component below.
export default function Browse() {
  const { type } = useParams();
  if (type === "live_tv") return <LiveTv />;
  return <BrowseGrid />;
}

function BrowseGrid() {
  const { type } = useParams();
  const navigate = useNavigate();
  // v1.3.72 — read the optional `?series=...` query param so the
  // global Search screen can deep-link straight to a series drill-
  // down. We only read it once per mount; once the user clicks
  // around the URL changes via push and we don't override their
  // navigation.
  const initialSeriesParam = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get("series") || "";
    } catch { return ""; }
  }, []);
  const { playlists } = useOutletContext();
  const { channels, status, epgByChannel, xtream, b2c, playlist } = usePlaylistData();

  const showFavorites = isFavoritable(type);
  const favCount = showFavorites ? favoritesCount(type) : 0;
  const showHistory = CONTINUE_WATCHING_TYPES.has(type);

  // v1.4.26 — Smart-TV D-pad handler for the categories sidebar.
  //
  // On Android TV / Fire TV / Google TV the remote D-pad emits
  // `keydown ArrowDown` / `ArrowUp` / `PageDown` / `PageUp` events.
  // Chromium's default handling for those keys is to SCROLL the
  // container, not to move focus between focusable buttons —
  // meaning the user could see "ABC", "Amazon Prime", "AMC" listed
  // below the currently-highlighted category but pressing Down did
  // nothing because focus stayed pinned. Symptom in the video: the
  // highlight sticks on "Recently episodes added" and the user
  // cannot reach any of the show categories that follow.
  //
  // Fix: intercept the D-pad keys, find all `<button>` elements
  // inside the sidebar, and move focus to the next / previous one.
  // Focusing a button automatically scrolls it into view (browsers
  // call `scrollIntoView({ block: "nearest" })` on `focus()` for
  // elements inside `overflow: auto` containers), so the list
  // scrolls organically as the user drives the D-pad.
  const handleSidebarKey = useCallback((e) => {
    const key = e.key;
    // v1.4.31 — ArrowRight on a sidebar button on a Smart TV must
    // jump focus INTO the main content grid (first tile / series
    // aggregate / episode). Chromium's spatial-nav otherwise picks
    // the nearest visually-right element which is often the wrong
    // thing (another sidebar entry, or the top-nav bar).
    if (key === "ArrowRight" || key === "Enter") {
      // Only take over ArrowRight — Enter still fires the button's
      // onClick to actually set activeGroup, and THEN we hop to the
      // tiles on the next render. For Enter, defer with rAF.
      const focusFirstTile = () => {
        const mainRoot = document.querySelector('[data-browse-main]');
        if (!mainRoot) return false;
        const first = mainRoot.querySelector(
          '[data-testid^="series-tile-"] button, [data-testid^="player-content-"] button, [data-testid^="pro-live-row-"], [role="button"]:not([tabindex="-1"])'
        );
        if (first) {
          try { first.focus({ preventScroll: true }); } catch { first.focus(); }
          try { first.scrollIntoView({ block: "center" }); } catch { /* older WebViews */ }
          return true;
        }
        return false;
      };
      // v1.4.38 — After Enter/ArrowRight, the tiles may not be
      // rendered yet: the group's items are pulled from IDB and can
      // take 100–500 ms on Smart TVs with slow storage. Instead of a
      // single rAF hop, poll up to ~1.5 s (30 × 50 ms) and stop as
      // soon as the first tile appears. Same for ArrowRight in case
      // the user pressed it while tiles were still hydrating from a
      // just-selected category.
      const pollFocus = (attempts = 30, delayMs = 50) => {
        if (focusFirstTile()) return;
        if (attempts <= 0) return;
        setTimeout(() => pollFocus(attempts - 1, delayMs), delayMs);
      };
      if (key === "ArrowRight") {
        // Try immediately (in case tiles are already there) — if not,
        // poll. Always preventDefault to stop Chromium's spatial-nav
        // from moving focus to a random neighbour while we wait.
        e.preventDefault();
        e.stopPropagation();
        if (!focusFirstTile()) pollFocus();
        return;
      } else if (key === "Enter") {
        // Let the button's own onClick run first (mutates activeGroup),
        // then poll for tiles on subsequent frames.
        requestAnimationFrame(() => pollFocus());
      }
    }
    if (
      key !== "ArrowDown" && key !== "ArrowUp" &&
      key !== "PageDown" && key !== "PageUp" &&
      key !== "Home" && key !== "End"
    ) return;
    const container = e.currentTarget;
    const focusables = Array.from(
      container.querySelectorAll('button:not([disabled])')
    );
    if (focusables.length === 0) return;
    const active = document.activeElement;
    const idx = focusables.indexOf(active);
    let next = idx;
    if (key === "ArrowDown") next = idx < 0 ? 0 : Math.min(focusables.length - 1, idx + 1);
    else if (key === "ArrowUp") next = idx < 0 ? focusables.length - 1 : Math.max(0, idx - 1);
    else if (key === "PageDown") next = idx < 0 ? 0 : Math.min(focusables.length - 1, idx + 6);
    else if (key === "PageUp") next = idx < 0 ? 0 : Math.max(0, idx - 6);
    else if (key === "Home") next = 0;
    else if (key === "End") next = focusables.length - 1;
    if (next === idx || next < 0) return;
    e.preventDefault();
    e.stopPropagation();
    const el = focusables[next];
    try { el.focus({ preventScroll: true }); } catch { el.focus(); }
    try { el.scrollIntoView({ block: "nearest" }); } catch { /* older WebViews */ }
  }, []);

  // v1.4.39 — Smart-TV D-pad handler for the main tile grid.
  //
  // Chromium's default spatial-nav on Smart-TV WebViews is unreliable
  // once tiles carry hover rings, focus scale transforms, or absolute-
  // positioned overlay children (fav toggles, ✕ buttons on CW rows).
  // We take over ArrowLeft/Right/Up/Down and walk the visually-linear
  // list of tile buttons ourselves. Columns are inferred from the
  // grid's computed style.
  //
  // v1.4.40 — Walk TILE DIVS (not buttons). Each tile has multiple
  // direct-child buttons (main + ✕ quick-remove + ⭐ fav toggle);
  // grabbing every one bloated the index space and made ArrowDown
  // skip whole rows. Now we take the first `<button>` per tile div,
  // and use `.closest()` to find which tile currently owns focus.
  const gridRef = useRef(null);
  const handleGridKey = useCallback((e) => {
    const key = e.key;
    if (
      key !== "ArrowLeft" && key !== "ArrowRight" &&
      key !== "ArrowUp"   && key !== "ArrowDown" &&
      key !== "Home"      && key !== "End"
    ) return;
    // v1.4.42 — Only intercept when focus is actually on a TILE (or
    // one of its overlay children like ✕ / ⭐). If focus is on the
    // in-category search input, the top tabs, or the category sidebar,
    // the arrow keys must fall through to the browser / that widget's
    // own handler — otherwise typing / cursor movement in the search
    // field jumps focus to a random tile (bug reported in v1.4.41).
    const focusedTile = document.activeElement?.closest(
      '[data-testid^="series-tile-"], [data-testid^="player-content-"]'
    );
    if (!focusedTile) return;
    // One tile div per poster.
    const container = e.currentTarget;
    const tileDivs = Array.from(
      container.querySelectorAll(
        '[data-testid^="series-tile-"], [data-testid^="player-content-"]'
      )
    );
    if (tileDivs.length === 0) return;
    let idx = tileDivs.indexOf(focusedTile);
    if (idx < 0) idx = 0;
    // Infer column count from the tile div's PARENT grid. Get its
    // resolved `grid-template-columns` and count entries.
    const gridEl = tileDivs[0].parentElement;
    let cols = 1;
    if (gridEl) {
      const gcs = getComputedStyle(gridEl).gridTemplateColumns || "";
      cols = Math.max(1, gcs.trim().split(/\s+/).filter(Boolean).length);
    }
    let next = idx;
    // v1.0.5 — SPATIAL up/down navigation. Instead of trusting the
    // grid's `grid-template-columns` (which lies when tiles have
    // different widths, when the grid uses auto-fill/auto-fit, or
    // when a virtualized row is partially populated) we walk the
    // real bounding rects of every rendered tile and pick the one
    // whose center-X is closest to ours in the NEXT row above/below.
    // This is what the TV remote user visually expects: pressing
    // Down goes STRAIGHT DOWN, never diagonally.
    const spatialPick = (direction /* "up" | "down" */) => {
      const cur = tileDivs[idx].getBoundingClientRect();
      const curCX = cur.left + cur.width / 2;
      const curCY = cur.top + cur.height / 2;
      let best = -1;
      let bestDist = Infinity;
      for (let i = 0; i < tileDivs.length; i++) {
        if (i === idx) continue;
        const r = tileDivs[i].getBoundingClientRect();
        if (r.width === 0 || r.height === 0) continue;
        const rCX = r.left + r.width / 2;
        const rCY = r.top + r.height / 2;
        // Must be strictly ABOVE or BELOW (not the same row).
        // Threshold on half-height so tiles on the same row don't
        // count as "below" due to a 1-2px rounding wobble.
        const dy = rCY - curCY;
        if (direction === "down" && dy <= cur.height * 0.4) continue;
        if (direction === "up"   && dy >= -cur.height * 0.4) continue;
        const dx = Math.abs(rCX - curCX);
        // Weight horizontal distance heavily — Down should go straight
        // down first, only picking a diagonal if the row is otherwise
        // empty at that column.
        const dist = dx * 4 + Math.abs(dy);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best >= 0 ? best : idx;
    };
    if (key === "ArrowRight") next = Math.min(tileDivs.length - 1, idx + 1);
    else if (key === "ArrowLeft") {
      if (idx % cols === 0) {
        // Hop back to the currently-active category button.
        const sidebar = document.querySelector('[data-testid="player-categories-sidebar"]');
        const active = sidebar?.querySelector(
          '[data-testid^="player-group-"][class*="orange"], [data-testid^="player-group-"][class*="amber"], [data-testid^="player-group-"][class*="emerald"], [data-testid^="player-group-"][class*="sky"]'
        ) || sidebar?.querySelector('[data-testid^="player-group-"]');
        if (active) {
          e.preventDefault();
          e.stopPropagation();
          try { active.focus({ preventScroll: true }); } catch { active.focus?.(); }
          try { active.scrollIntoView({ block: "nearest" }); } catch { /* */ }
          return;
        }
        return;
      }
      next = Math.max(0, idx - 1);
    } else if (key === "ArrowUp") next = spatialPick("up");
    else if (key === "ArrowDown") next = spatialPick("down");
    else if (key === "Home") next = 0;
    else if (key === "End") next = tileDivs.length - 1;
    // Even if `next === idx` (edge of grid), still consume the event
    // so Chromium's spatial-nav doesn't yank us into the top nav.
    e.preventDefault();
    e.stopPropagation();
    if (next === idx) return;
    // Focus the MAIN button of the target tile (first <button> child).
    const targetTile = tileDivs[next];
    const btn = targetTile.querySelector("button");
    if (!btn) return;
    // v1.4.52 — Use preventScroll:true to prevent Android WebView from
    // firing an extra scroll+focus event (which was compounding with
    // our own scrollIntoView() call to trigger repeated focus events
    // on the same target, cascading into a runaway focus animation
    // that visually grew the tile). Our explicit scrollIntoView on
    // the next line handles positioning ourselves.
    try { btn.focus({ preventScroll: true }); } catch { btn.focus(); }
    try { btn.scrollIntoView({ block: "nearest" }); } catch { /* */ }
  }, []);

  const [activeGroup, setActiveGroup] = useState(null);
  // v1.0.7 — Faster category switching.
  //
  // Category clicks used to freeze the UI for 100-500 ms on large
  // playlists because a single click triggered synchronous work in
  // four useMemos (`itemsInGroup`, `seriesTiles`, ...) that iterate
  // every episode row.
  //
  // Fix: keep `activeGroup` synchronous so the sidebar highlight
  // paints instantly, but recompute the tile grid from a
  // `useDeferredValue`-wrapped copy. React 18/19 can then interrupt
  // the heavy render if the user immediately clicks another category
  // or types in the search field. Perceived latency drops from
  // "click-then-freeze" to "click-then-loading-hint" to done.
  const deferredActiveGroup = useDeferredValue(activeGroup);
  const isSwitchingGroup = deferredActiveGroup !== activeGroup;
  const [activeSeries, setActiveSeries] = useState(null); // series drill-down
  const [activeSeasonNum, setActiveSeasonNum] = useState(null); // selected tab in series drill-down
  const [activeMovie, setActiveMovie] = useState(null); // v1.3.1 movie drill-in
  // v1.0.8 — Sync activeSeries FROM the URL when the browser / TV
  // hardware Back button fires a popstate. Previously, `activeSeries`
  // lived in React state only. Pressing Back would pop `?series=` off
  // the URL but the state stayed set — the drill-down kept rendering
  // and the URL-sync effect below immediately re-pushed the param,
  // making Back feel like it did nothing (the "same page over and
  // over" symptom on the TV remote).
  useEffect(() => {
    if (type !== "series") return;
    const readSeriesFromUrl = () => {
      try {
        const sp = new URLSearchParams(window.location.search);
        return sp.get("series") || "";
      } catch { return ""; }
    };
    const onPop = () => {
      const wanted = readSeriesFromUrl();
      if (!wanted) {
        // URL no longer has ?series= → close the drill-down. State goes
        // null; the URL-sync effect will NOT re-push because
        // wasSeriesOpenRef flips to false first.
        wasSeriesOpenRef.current = false;
        setActiveSeries(null);
        setActiveSeasonNum(null);
      } else {
        // URL has a different series — remap state to it. Deep-link
        // hydration effect below will do the actual lookup once
        // seriesTiles is ready.
        if (!activeSeries || (activeSeries.series_name || "").toLowerCase() !== wanted.toLowerCase()) {
          // Reset the "used" guard so the deep-link effect can re-run
          // and hydrate activeSeries from the new URL param.
          seriesParamUsedRef.current = false;
        }
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, activeSeries]);

  // v1.3.1 — Same popstate contract for the MOVIE drill-in. Hardware
  // Back on the TV remote pops the pushState we made when opening the
  // drill; here we clear the state so the grid re-renders.
  useEffect(() => {
    if (type !== "movie") return;
    const onPop = () => {
      try {
        const sp = new URLSearchParams(window.location.search);
        if (!sp.get("movie")) {
          setActiveMovie(null);
        }
      } catch {
        setActiveMovie(null);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [type]);

  // v1.3.1 — Push a `?movie=<stream_url>` param when the drill-in
  // opens so hardware Back returns to the grid instead of unwinding
  // the whole player. Mirrors the pattern used for activeSeries.
  useEffect(() => {
    if (type !== "movie") return;
    try {
      const sp = new URLSearchParams(window.location.search);
      const cur = sp.get("movie") || "";
      if (activeMovie && activeMovie.stream_url) {
        if (cur !== activeMovie.stream_url) {
          sp.set("movie", activeMovie.stream_url);
          window.history.pushState(
            {},
            "",
            `${window.location.pathname}?${sp.toString()}`,
          );
        }
      } else if (!activeMovie && cur) {
        sp.delete("movie");
        const q = sp.toString();
        window.history.replaceState(
          {},
          "",
          window.location.pathname + (q ? `?${q}` : ""),
        );
      }
    } catch { /* noop */ }
  }, [type, activeMovie]);

  // Guard so the `?series=<name>` deep-link handler only runs once per
  // navigation. Cleared in the type/playlist reset effect below so a
  // fresh remount can re-hydrate the drill-down from the URL after the
  // user comes back from the Watch page.
  const seriesParamUsedRef = useRef(false);

  // v1.4.63 — Keep the current series drill-down encoded in the URL as
  // `?series=<name>` so navigating back from the /player/watch route
  // returns the user to the episode list they were in (not the top-
  // level Series grid). The existing deep-link effect in this file
  // already re-hydrates activeSeries from that param on remount, so
  // all we need is to keep the URL in sync while the user browses.
  //
  // v1.4.65 — Also mirror the currently-selected category into the URL
  // as `?group=<name>`. Previously the user could pick "Drama", open a
  // show, come back and land on "All" — the category selection was
  // lost. Now we round-trip it through the URL alongside `series`.
  //
  // v1.4.74 — When the drill-down OPENS (activeSeries goes from empty
  // to set) we now PUSH a new history entry instead of replacing.
  // Previously the browser / Android hardware Back key skipped past
  // the category grid and jumped straight to the Main Menu. Pushing
  // means Back pops back to the category view (which naturally
  // re-hydrates from the URL). All other URL sync updates still use
  // replace so we don't spam the history stack.
  const wasSeriesOpenRef = useRef(false);
  useEffect(() => {
    if (type !== "series") return;
    const sp = new URLSearchParams(window.location.search);
    const curSeries = sp.get("series") || "";
    const curGroup = sp.get("group") || "";
    let changed = false;
    // Sync ?series
    if (activeSeries && activeSeries.series_name) {
      if (curSeries.toLowerCase() !== activeSeries.series_name.toLowerCase()) {
        sp.set("series", activeSeries.series_name);
        changed = true;
      }
    } else if (curSeries) {
      sp.delete("series");
      changed = true;
    }
    // Sync ?group (skip pseudo groups like Favorites / Continue Watching —
    // those are stateful buckets, not real categories to restore on back).
    const groupParam =
      activeGroup && activeGroup !== ALL_GROUP && activeGroup !== FAV_GROUP && activeGroup !== HISTORY_GROUP && activeGroup !== CUE_GROUP
        ? activeGroup
        : "";
    if (groupParam) {
      if (curGroup !== groupParam) {
        sp.set("group", groupParam);
        changed = true;
      }
    } else if (curGroup) {
      sp.delete("group");
      changed = true;
    }
    // Push a new history entry when the drill-down *opens*, so a
    // subsequent browser/hardware Back returns the user to this
    // category — not to the Main Menu.
    const opening = !!activeSeries && !wasSeriesOpenRef.current;
    wasSeriesOpenRef.current = !!activeSeries;
    if (changed) {
      const qs = sp.toString();
      navigate(
        { pathname: window.location.pathname, search: qs ? `?${qs}` : "" },
        { replace: !opening },
      );
    }
  }, [activeSeries, activeGroup, type, navigate]);
  // v1.4.20 — Persist the season/episode sort inside a series drill-down
  // too. Previously this was `useState("se")` with no persistence, so users
  // who picked "A-Z" on a show, played an episode, and came back found the
  // sort back on "SE order". Now we mirror the main-sort behaviour.
  const readStoredEpSort = () => {
    try { return localStorage.getItem("zapp_ep_sort") || null; } catch { return null; }
  };
  const [seasonEpisodeSort, setSeasonEpisodeSortRaw] = useState(() => readStoredEpSort() || "se");
  const setSeasonEpisodeSort = useCallback((next) => {
    setSeasonEpisodeSortRaw(next);
    try { localStorage.setItem("zapp_ep_sort", next); } catch { /* private-mode */ }
  }, []);
  const [infoItem, setInfoItem] = useState(null); // legacy — no longer used, kept for context menu
  // v1.4.20 — Sort choice must persist until the user changes it — including
  // across group switches, prewarm finishes, and app restarts. We keep one
  // key per browse type so Movies / Series / Live TV each remember their
  // own last-picked sort. `readStoredSort` returns null when nothing is
  // stored so the type-default effect below still fires on first-ever use.
  const readStoredSort = (t) => {
    try { return localStorage.getItem(`zapp_sort_${t}`) || null; } catch { return null; }
  };
  const [sort, setSort] = useState(() => readStoredSort(type) || "m3u");
  const setSortPersisted = useCallback((next) => {
    setSort(next);
    try { localStorage.setItem(`zapp_sort_${type}`, next); } catch { /* private-mode */ }
  }, [type]);
  const [favTick, setFavTick] = useState(0);
  // v1.0.9 — Movie Cue reactivity. Bumps when addToCue/removeFromCue
  // fires the "movie-cue-changed" event so tiles + sidebar counter
  // re-render without a manual reload.
  const [cueTick, setCueTick] = useState(0);
  useEffect(() => {
    const on = () => setCueTick((t) => t + 1);
    window.addEventListener("movie-cue-changed", on);
    return () => window.removeEventListener("movie-cue-changed", on);
  }, []);
  // v1.3.53 — bump when watched-items registry changes so tiles re-render
  // with the ✓ overlay after the user returns from the player.
  const [watchedTick, setWatchedTick] = useState(0);
  useEffect(() => {
    const off = subscribeWatched(() => setWatchedTick((t) => t + 1));
    return off;
  }, []);
  // v1.4.30 — Context menu (long-press / right-click) state. When set,
  // <TileContextMenu> pops open at (x, y) with the item-specific action
  // list. Cleared when the user picks an action or dismisses the menu.
  const [tileMenu, setTileMenu] = useState(null);
  const closeTileMenu = useCallback(() => setTileMenu(null), []);
  // v1.4.30 — When the user opens a show from Continue Watching we want
  // the drill-down to scroll straight to the last-watched episode and
  // highlight it. Populated by `handleSeriesTileClick` for CW pseudo
  // tiles; consumed by the drill-down render + a scroll-into-view
  // effect further down.
  const [resumeEpisodeUrl, setResumeEpisodeUrl] = useState(null);
  // Mobile sidebar drawer (phones / narrow tablets) — sidebar lives on the
  // left at `lg` and above, slides in from the left below that.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // In-category search box (Movies / Series tabs). Filters by item name,
  // case-insensitively. Reset whenever the category or type changes so the
  // user never lands on an empty list from a stale query.
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    setSearchQuery("");
  }, [activeGroup, type]);

  // Lazy-loaded series-level metadata for the drill-down view (plot, cast,
  // director, rating). `seriesInfo === null` means "not loaded / no data";
  // we never block the UI on this — the page renders with what we already
  // have and metadata pops in once the provider responds. When the provider
  // info includes a `tmdb_id`, we ALSO call our TMDB proxy and let it fill
  // any fields the provider left empty (poster, plot, cast, etc).
  const [seriesInfo, setSeriesInfo] = useState(null);
  const [seriesTmdb, setSeriesTmdb] = useState(null);
  const [loadingSeriesInfo, setLoadingSeriesInfo] = useState(false);
  // Lazy-fetched B2C episodes — keyed by series_id. Populated on first
  // tile open so the catalog import stays fast (the old eager drill
  // was the 15-minute bottleneck users complained about). Persisted
  // in component state so re-opening the same series during a
  // session is instant.
  const [b2cEpisodes, setB2cEpisodes] = useState({});
  const [loadingB2cEpisodes, setLoadingB2cEpisodes] = useState(false);

  // v1.3.66 — Xtream JSON-API episodes (ITVZA-style). When the active
  // playlist is Xtream and the tile carries a `series_id`, we fetch
  // the full episode list directly from `get_series_info` instead of
  // relying on the M3U parser's series-grouping output. This solves
  // the "Kelders van Geheime S2E295–299 missing" P0 bug: the M3U
  // parser was silently dropping the tail of huge playlists, but
  // `get_series_info` returns episodes straight from the provider's
  // database — always fresh, always complete, scoped to one series.
  //
  // Result is also persisted to IDB (`series_episodes` store) with
  // a 30-minute TTL so re-opening the same series after a cold start
  // is instant.
  const [xtreamEpisodes, setXtreamEpisodes] = useState({});
  const [loadingXtreamEpisodes, setLoadingXtreamEpisodes] = useState(false);

  useEffect(() => {
    if (!activeSeries || !b2c?.base || !b2c?.token) return;
    if (!activeSeries.series_id) return;
    const sid = activeSeries.series_id;
    // Skip if we've already pulled episodes for this series.
    if (b2cEpisodes[sid]) return;
    // Skip if the tile carries REAL episode rows (e.g. cached from an
    // older catalog version, or Xtream catalogs that ship episodes
    // eagerly).
    const hasRealEpisodes = (activeSeries.episodes || []).some(
      (ep) => ep && !ep._b2c_placeholder && ep.stream_url
    );
    if (hasRealEpisodes) return;
    let cancelled = false;
    setLoadingB2cEpisodes(true);
    fetchB2cSeriesEpisodes({ base: b2c.base }, b2c.token, sid)
      .then((res) => {
        if (cancelled) return;
        setB2cEpisodes((prev) => ({ ...prev, [sid]: res?.episodes || [] }));
      })
      .catch(() => { /* silent — user will see "no episodes" UI */ })
      .finally(() => {
        if (!cancelled) setLoadingB2cEpisodes(false);
      });
    return () => { cancelled = true; };
  }, [activeSeries, b2c, b2cEpisodes]);

  // v1.3.66 — Xtream JSON-API episodes loader. Fires when the user
  // opens a series tile on an Xtream playlist (creds present + a
  // `series_id` on the tile). Reads from the IDB cache first, then
  // falls back to a live `get_series_info` call. The result is
  // persisted to IDB so the next cold start hits the cache.
  //
  // This path completely bypasses the M3U parser for the episode
  // list, which is what fixes the "S2E295–299 missing" bug — the
  // provider's JSON DB is the source of truth, not the M3U file
  // tail.
  useEffect(() => {
    if (!activeSeries || !xtream?.creds) return undefined;
    const sid = activeSeries.series_id;
    if (!sid) return undefined;
    // Skip if we've already loaded this series during this session.
    if (xtreamEpisodes[sid]) return undefined;
    let cancelled = false;
    const plId = playlist?.id;

    (async () => {
      // 1. Try IDB cache first — instant on cold start when fresh.
      const cached = plId ? await getCachedSeriesEpisodes(plId, sid) : null;
      if (cancelled) return;
      if (cached && Array.isArray(cached.episodes) && cached.episodes.length > 0) {
        setXtreamEpisodes((prev) => ({ ...prev, [sid]: cached.episodes }));
        // We still fire a background refresh below if the cache is
        // close to TTL — but the user sees content immediately.
      } else {
        setLoadingXtreamEpisodes(true);
      }
      // 2. Live fetch from `get_series_info`.
      try {
        const res = await fetchXtreamSeriesEpisodes(xtream.creds, sid);
        if (cancelled) return;
        if (res && Array.isArray(res.episodes) && res.episodes.length > 0) {
          setXtreamEpisodes((prev) => ({ ...prev, [sid]: res.episodes }));
          if (plId) await cacheSeriesEpisodes(plId, sid, res);
        }
      } catch {
        /* silent — user will fall back to M3U-derived episodes */
      } finally {
        if (!cancelled) setLoadingXtreamEpisodes(false);
      }
    })();

    return () => { cancelled = true; };
  }, [activeSeries, xtream, xtreamEpisodes, playlist?.id]);

  // Effective episode list for the drill-down: prefer freshly-fetched
  // Xtream JSON episodes (most authoritative, always complete), then
  // B2C lazy-fetched episodes, then whatever the tile shipped with
  // (M3U-derived fallback).
  const activeSeriesEpisodes = useMemo(() => {
    if (!activeSeries) return [];
    const sid = activeSeries.series_id;
    const xtreamFetched = sid ? xtreamEpisodes[sid] : null;
    if (xtreamFetched && xtreamFetched.length > 0) return xtreamFetched;
    const b2cFetched = sid ? b2cEpisodes[sid] : null;
    if (b2cFetched && b2cFetched.length > 0) return b2cFetched;
    // Filter out placeholder rows so the UI doesn't render an empty
    // "S? E?" entry while we're loading.
    return (activeSeries.episodes || []).filter((ep) => !ep?._b2c_placeholder);
  }, [activeSeries, b2cEpisodes, xtreamEpisodes]);

  useEffect(() => {
    if (!activeSeries) {
      setSeriesInfo(null);
      setSeriesTmdb(null);
      return;
    }
    let cancelled = false;
    setSeriesInfo(null);
    setSeriesTmdb(null);

    // B2C catalogs ship the series metadata inline on the tile
    // (cover, plot, cast, director, genre, release_date, rating) — see
    // `oneStreamApi.fetchB2cCatalog`. When present, surface it as
    // seriesInfo immediately so the drill-down hero / synopsis don't
    // wait on an Xtream API call that will never happen for B2C
    // sources.
    if (activeSeries.b2cInfo) {
      setSeriesInfo(activeSeries.b2cInfo);
    }

    // Pre-fire TMDB if the bulk Xtream enrichment already captured the id —
    // populates the header instantly, no need to wait for get_series_info.
    if (activeSeries.tmdb_id) {
      fetchTmdbTv(activeSeries.tmdb_id).then((t) => {
        if (!cancelled) setSeriesTmdb(t || null);
      });
    } else {
      // No tmdb_id known up front — fall back to a name+year TMDB lookup
      // so the drill-down still gets a poster + plot. This is the only
      // source of details for Plex M3U series that never appear in the
      // Xtream get_series catalog (i.e. provider exposes them as plain
      // M3U entries with no series_id). Was previously gated behind
      // `b2cInfo` but the B2C overlay was removed in v1.3.16, so the
      // gate now fires for every series.
      const yr =
        activeSeries.year ||
        (activeSeries.b2cInfo?.release_date?.slice?.(0, 4)
          ? parseInt(activeSeries.b2cInfo.release_date.slice(0, 4), 10)
          : null);
      fetchTmdbTvByName?.(activeSeries.series_name, yr)?.then?.((t) => {
        if (!cancelled && t) setSeriesTmdb(t);
      });
    }

    // Then still call the provider for episode list + provider-side overrides.
    if (xtream?.creds && activeSeries.series_id) {
      setLoadingSeriesInfo(true);
      fetchSeriesInfo(xtream.creds, activeSeries.series_id)
        .then((d) => {
          if (cancelled) return;
          const info = d?.info || null;
          setSeriesInfo(info);
          // Fall back to fetching TMDB now if the bulk listing missed it.
          const tmdbId =
            info?.tmdb_id || info?.tmdb || activeSeries.tmdb_id;
          if (tmdbId && !activeSeries.tmdb_id) {
            fetchTmdbTv(tmdbId).then((t) => {
              if (!cancelled) setSeriesTmdb(t || null);
            });
          }
        })
        .catch(() => {
          if (!cancelled) setSeriesInfo(null);
        })
        .finally(() => {
          if (!cancelled) setLoadingSeriesInfo(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [activeSeries, xtream]);

  // Fast lookup from stream_url → live channel record so Favorites and
  // Continue Watching can use the up-to-date poster / metadata even when
  // the originally-stored entry didn't have one (e.g. favorited before
  // Xtream enrichment kicked in).
  const channelByUrl = useMemo(() => {
    const m = new Map();
    for (const c of channels) m.set(c.stream_url, c);
    return m;
  }, [channels]);

  const hydrate = (it) => {
    const live = channelByUrl.get(it.stream_url);
    if (!live) return it;
    return {
      ...it,
      logo_url: it.logo_url || live.logo_url || null,
      year: it.year || live.year,
      plot: it.plot || live.plot,
      rating: it.rating || live.rating,
      group: it.group || live.group,
      series_name: it.series_name || live.series_name,
      season: it.season || live.season,
      episode: it.episode || live.episode,
      added_ts: live.added_ts || it.added_ts,
    };
  };

  // v1.4.30 — Continue Watching row: uses the aggregation-aware helper
  // in watchHistory. For movies/catchup the result is one entry per
  // stream_url (filtered to items with ≥2 min left). For series it's
  // one aggregate per SHOW (latest-watched episode only), so the sidebar
  // count matches the number of distinct titles shown to the user.
  const [historyTick, setHistoryTick] = useState(0);
  const bumpHistoryTick = useCallback(() => setHistoryTick((n) => n + 1), []);
  const continueWatchingRows = useMemo(() => {
    if (!showHistory) return [];
    // For series the rows are aggregate — no kids filter (which only
    // looks at name/group) is needed at this layer.
    if (type === "series") return getContinueWatching("series");
    // For movies / catchup, hydrate + kids-filter individual items.
    const rows = getContinueWatching(type);
    return rows
      .map((r) => ({ ...r, item: hydrate(r.item) }))
      .filter((r) => {
        const it = r.item;
        return filterForKids([it]).length > 0;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, showHistory, channelByUrl, favTick, historyTick]);

  // Legacy `historyItems` and `seriesPosterLookup` are declared BELOW
  // `typedChannels` (see comment there) — moved to fix a TDZ crash
  // ("Cannot access 'fe' before initialization") when the production
  // bundle mangled variable names.
  const historyCount = continueWatchingRows.length;

  // v1.4.64 — Lookup of stream_url → { position_s, duration_s } for the
  // full watch history so we can render the "Continue Watching" progress
  // bar on ANY tile that carries partial progress — not just tiles
  // rendered under the Continue Watching sidebar entry. Previously a
  // half-watched movie showed nothing on the main grid (the Watched
  // badge only appears once <= 60 s remain, per v1.4.61), which the user
  // reported as "shows Nothing even if a few sec was only watched".
  const progressByUrl = useMemo(() => {
    const m = new Map();
    for (const it of getHistory()) {
      if (!it?.stream_url) continue;
      const pos = Number(it.position_s || 0);
      const dur = Number(it.duration_s || 0);
      if (pos <= 0) continue;
      m.set(it.stream_url, { position_s: pos, duration_s: dur });
    }
    return m;
    // historyTick changes whenever addToHistory / saveHistoryPosition
    // fires, so the map re-derives after the user returns from Watch.
  }, [historyTick]);

  // Channels for this type, optionally kids-filtered
  const typedChannels = useMemo(() => {
    const list = channels.filter((c) => c.content_type === type);
    const kidsOnly = isKidsActive()
      ? list.filter((c) => !isAdultText(c.name) && !isAdultText(c.group))
      : list;
    // Drop items belonging to user-hidden categories so the pseudo-groups
    // ("All", "Favorites", "Continue Watching") + search results also respect
    // the hide list, not just the sidebar.
    const hidden = getHiddenCategories(playlist?.id || "default", type);
    if (!hidden.size) return kidsOnly;
    return kidsOnly.filter((c) => !hidden.has(c.group || "Uncategorized"));
  }, [channels, type, playlist?.id]);

  // v1.4.73 — Show-poster lookup used to backfill Continue Watching
  // pseudo-tiles when the individual episode row (that seeded history)
  // had no `logo_url`. Keyed by both series_id and lower-cased
  // series_name so both watch-history shapes hit. Declared AFTER
  // `typedChannels` to avoid a TDZ crash in the production bundle.
  const seriesPosterLookup = useMemo(() => {
    if (type !== "series") return null;
    const byId = new Map();
    const byName = new Map();
    for (const ep of typedChannels) {
      const poster = ep.logo_url || null;
      if (!poster) continue;
      const sid =
        ep.xc_kind === "series" && ep.xc_id ? String(ep.xc_id) : null;
      if (sid && !byId.has(sid)) byId.set(sid, poster);
      const nm = (ep.series_name || ep.name || "").toLowerCase();
      if (nm && !byName.has(nm)) byName.set(nm, poster);
    }
    return { byId, byName };
  }, [type, typedChannels]);

  // Legacy `historyItems` name: flat list of items (movies) OR pseudo
  // series-tile objects for the series branch. Downstream render paths
  // still key off this — we just change what's in it depending on type.
  const historyItems = useMemo(() => {
    if (type === "series") {
      return continueWatchingRows.map((r) => {
        // Backfill missing poster with the parent show's cover.
        let showPoster = r.poster_url || r.logo_url || null;
        if (!showPoster && seriesPosterLookup) {
          if (r.series_id) {
            showPoster =
              seriesPosterLookup.byId.get(String(r.series_id)) || null;
          }
          if (!showPoster && r.series_name) {
            showPoster =
              seriesPosterLookup.byName.get(r.series_name.toLowerCase()) ||
              null;
          }
        }
        return {
          __cw: true,
          series_id: r.series_id,
          series_name: r.series_name,
          poster: showPoster,
          logo_url: showPoster,
          episodes: [],
          latest: r.latest,
        };
      });
    }
    return continueWatchingRows.map((r) => r.item);
  }, [type, continueWatchingRows, seriesPosterLookup]);

  // Groups for the sidebar
  const groups = useMemo(() => {
    // Pass the tab `type` (live_tv | movie | series | catchup) so
    // `groupChannels` can count UNIQUE shows per category for Series
    // instead of raw episode rows. `typedChannels` is already filtered
    // by type but the second arg also enables the series-aware
    // deduplication logic.
    let gs = groupChannels(typedChannels, type);
    if (isKidsActive()) gs = gs.filter((g) => !isAdultText(g.name));
    // Hide-categories filter (mirrors VU Player Pro's hide-category UI).
    // Read on every render so toggling in Settings → Hide Categories takes
    // effect as soon as the user comes back to Browse.
    const hidden = getHiddenCategories(playlist?.id || "default", type);
    if (hidden.size) gs = gs.filter((g) => !hidden.has(g.name));
    return gs;
  }, [typedChannels, type, playlist?.id]);

  // ----- Per-category lazy load (v1.2.16) ------------------------------
  // For real category picks (anything other than the pseudo-groups
  // ALL/FAV/HISTORY) we pull JUST that category's rows from IndexedDB
  // instead of filtering the giant in-memory `channels` array. On a
  // 200k-channel playlist this drops the per-click filter cost from
  // ~150 ms to ~12 ms because the IDB cursor walks the compound
  // `[playlist_key, content_type, group]` index instead of scanning
  // every row.
  // ----- Per-category lazy load (v1.2.16) -- v1.2.17: now strictly
  // additive so we NEVER serve fewer items than the in-memory filter.
  // The compound IndexedDB index drops records whose `group` field is
  // undefined / null at write time (e.g. Plex backed channels), so the
  // IDB cursor walk can come back short. We only swap in the lazy
  // result when its row-count matches (or beats) the in-memory filter.
  const [lazyItems, setLazyItems] = useState(null);
  useEffect(() => {
    if (
      !playlist?.id ||
      !activeGroup ||
      activeGroup === ALL_GROUP ||
      activeGroup === FAV_GROUP ||
      activeGroup === HISTORY_GROUP
    ) {
      setLazyItems(null);
      return;
    }
    const groupKey = activeGroup === "Uncategorized" ? "" : activeGroup;
    // What does the in-memory filter say the count should be? If the IDB
    // result lands lower than this, we know the compound index missed
    // some records (typically Plex-backed channels with no group_title
    // — IDB compound indexes silently skip undefined key parts). In
    // that case we DROP the lazy result and stick with the in-memory
    // filter so posters + metadata always render.
    const expected = typedChannels.filter((c) => {
      if (activeGroup === "Uncategorized") return !c.group;
      return c.group === activeGroup;
    }).length;
    let cancelled = false;
    getChannelsByGroup(playlist.id, type, groupKey)
      .then((rows) => {
        if (cancelled) return;
        if (rows.length < expected) {
          // Lazy load is missing items — fall back to the in-memory
          // filter so the user never sees a half-populated grid.
          setLazyItems(null);
          return;
        }
        setLazyItems(filterForKids(rows));
      })
      .catch(() => {
        if (!cancelled) setLazyItems(null);
      });
    return () => {
      cancelled = true;
    };
  }, [playlist?.id, type, activeGroup, typedChannels]);

  // ----- v1.3.0 SQL pre-aggregated tiles ---------------------------
  // For Series: `series_tiles` table has one row per SHOW with the
  // poster already resolved at import time. Avoids the ~1s freeze on
  // TV boxes caused by the JS Map walk over 10k+ episodes on every
  // category click.
  //
  // For Movies: `movie_tiles` table pre-resolves `poster_url` so we
  // don't lose 30% of tiles to blank placeholders on fast scroll.
  //
  // Both are NULL until SQL is available AND the tile query has
  // returned. Falls back to the legacy in-memory path when null.
  const [sqlSeriesTiles, setSqlSeriesTiles] = useState(null);
  const [sqlMovieTiles, setSqlMovieTiles] = useState(null);
  useEffect(() => {
    if (!playlist?.id) {
      setSqlSeriesTiles(null);
      setSqlMovieTiles(null);
      return;
    }
    // Pseudo-groups (Favorites / Continue Watching / Cue) still use
    // their JS-side sources. SQL tiles cover ALL_GROUP and every
    // real category name.
    const groupArg =
      !deferredActiveGroup ||
      deferredActiveGroup === FAV_GROUP ||
      deferredActiveGroup === HISTORY_GROUP ||
      deferredActiveGroup === CUE_GROUP
        ? null
        : deferredActiveGroup === ALL_GROUP
        ? "__all__"
        : deferredActiveGroup;
    let cancelled = false;
    if (type === "series" && groupArg) {
      getSeriesTiles(playlist.id, groupArg)
        .then((tiles) => {
          if (cancelled) return;
          setSqlSeriesTiles(Array.isArray(tiles) ? tiles : null);
        })
        .catch(() => { if (!cancelled) setSqlSeriesTiles(null); });
    } else if (type === "series") {
      setSqlSeriesTiles(null);
    }
    if (type === "movie" && groupArg) {
      getMovieTiles(playlist.id, groupArg)
        .then((tiles) => {
          if (cancelled) return;
          setSqlMovieTiles(Array.isArray(tiles) ? tiles : null);
        })
        .catch(() => { if (!cancelled) setSqlMovieTiles(null); });
    } else if (type === "movie") {
      setSqlMovieTiles(null);
    }
    return () => {
      cancelled = true;
    };
  }, [playlist?.id, type, deferredActiveGroup]);

  // ----- v1.3.66 Phase 2 — Movies via Xtream JSON API (ITVZA-style)
  //
  // When the active playlist has Xtream credentials AND the user is
  // on the Movies tab AND has picked a real (non-pseudo) category,
  // we fetch the movie list straight from `get_vod_streams?category_id=N`
  // and use it as the authoritative source.
  //
  // Why this is an additive win, not a replacement:
  //   • Bypasses the M3U parser → no more tail-drop bug for movies
  //     (mirrors the Phase 1 fix for series).
  //   • Server-side filtering by `category_id` keeps the per-open
  //     network payload tiny (~80 KB vs ~12 MB for full VOD list on
  //     a 50k-movie provider).
  //   • Cached to IDB (1h TTL) so re-opens are instant and a
  //     background refresh keeps catalog fresh as new movies
  //     arrive upstream.
  //
  // Mapping: the M3U sidebar gives us `activeGroup` as a category
  // *name* (group_title). We fetch `get_vod_categories` (cached 24h)
  // to translate that to a `category_id` for the per-category call.
  // Most providers use the same string for both — exact-match works.
  // If we can't find a matching category, we silently fall back to
  // the M3U path (no UX change for the user).
  const [xtreamMovies, setXtreamMovies] = useState(null);
  const [xtreamVodCats, setXtreamVodCats] = useState(null);
  useEffect(() => {
    if (
      type !== "movie" ||
      !xtream?.creds ||
      !playlist?.id ||
      !activeGroup ||
      activeGroup === ALL_GROUP ||
      activeGroup === FAV_GROUP ||
      activeGroup === HISTORY_GROUP
    ) {
      setXtreamMovies(null);
      return undefined;
    }
    let cancelled = false;
    const plId = playlist.id;
    const creds = xtream.creds;
    (async () => {
      // 1. Resolve Xtream VOD categories — used to map name → id.
      let cats = xtreamVodCats;
      if (!cats) {
        cats = await getCachedVodCategories(plId);
        if (!cats) {
          try {
            const fresh = await fetchXtreamVodCategories(creds);
            if (Array.isArray(fresh)) {
              cats = fresh;
              await cacheVodCategories(plId, fresh);
            }
          } catch { /* silent */ }
        }
        if (cancelled) return;
        if (cats) setXtreamVodCats(cats);
      }
      if (!cats || cats.length === 0) {
        // No category list — can't do a precise per-category fetch.
        // Fall through to the M3U path.
        return;
      }
      // 2. Find the category_id whose name matches activeGroup.
      // "Uncategorized" is a special M3U pseudo-group that doesn't
      // exist in Xtream — leave the M3U path to handle it.
      let targetCatId = null;
      let targetCatName = null;
      if (activeGroup !== "Uncategorized") {
        const match = cats.find((c) => c.category_name === activeGroup);
        if (match) {
          targetCatId = match.category_id;
          targetCatName = match.category_name;
        }
      }
      if (!targetCatId) return;

      // 3. IDB cache → instant.
      const cached = await getCachedVodStreams(plId, targetCatId);
      if (cancelled) return;
      if (Array.isArray(cached) && cached.length > 0) {
        setXtreamMovies(filterForKids(cached));
      }
      // 4. Live refresh (always — TTL gates the cache freshness, the
      // user always gets the latest server-side movie list when they
      // actually open the category).
      try {
        const fresh = await fetchXtreamVodStreams(creds, targetCatId, undefined, targetCatName);
        if (cancelled) return;
        if (Array.isArray(fresh) && fresh.length > 0) {
          setXtreamMovies(filterForKids(fresh));
          await cacheVodStreams(plId, targetCatId, fresh);
        } else if (!cached) {
          // Provider returned empty / null — keep null so the M3U
          // fallback path renders instead of an empty grid.
          setXtreamMovies(null);
        }
      } catch {
        // Network failure — keep cached (if any) or fall through.
        if (!cached) setXtreamMovies(null);
      }
    })();
    return () => { cancelled = true; };
  }, [playlist?.id, type, activeGroup, xtream, xtreamVodCats]);

  // Flat items within the active category (used for Live TV + Movies)
  const itemsInGroup = useMemo(() => {
    // v1.0.7 — Read the DEFERRED group name so this memo (which walks
    // every channel row) rides the low-priority transition. The
    // sidebar highlight still uses `activeGroup` for instant feedback.
    if (deferredActiveGroup === FAV_GROUP) {
      // Look up the live channel record so posters/year/etc come through even
      // if the favorite was saved before Xtream enrichment populated them.
      return filterForKids(getFavorites(type).map(hydrate));
    }
    if (deferredActiveGroup === HISTORY_GROUP) return historyItems;
    // v1.0.9 — Movie Cue: pull user-cued items in order (kids-safe).
    if (deferredActiveGroup === CUE_GROUP) {
      void cueTick;
      return filterForKids(getCue().map(hydrate));
    }
    // v1.0.9 — Movie Cue: pull user-cued items in order (kids-safe).
    if (deferredActiveGroup === CUE_GROUP) {
      void cueTick;
      return filterForKids(getCue().map(hydrate));
    }
    // "All Movies" / "All Series" pseudo-category — everything across every
    // group, kids-filter already applied by `typedChannels`.
    if (deferredActiveGroup === ALL_GROUP) return typedChannels;
    if (!deferredActiveGroup) return [];
    // v1.3.0 — For Movies, prefer the pre-aggregated SQL tiles. They
    // have `poster_url` already resolved so we skip the per-tile
    // TMDB waterfall + they come back from an indexed SELECT
    // instead of an O(n) filter of the 200k-row movies array.
    if (type === "movie" && Array.isArray(sqlMovieTiles)) {
      return filterForKids(sqlMovieTiles.map((t) => ({
        stream_url: t.stream_url,
        name: t.name,
        group: t.group_name,
        logo_url: t.poster_url,
        content_type: "movie",
        year: t.year,
        rating: t.rating,
        duration_s: t.duration_s,
        added_ts: t.added_ts,
      })));
    }
    // v1.3.66 Phase 2 — Xtream JSON-API movies for the active
    // category take precedence over the M3U-derived list. This
    // surfaces movies that the M3U parser may have dropped on huge
    // playlists (same root cause as the Phase 1 series fix).
    if (type === "movie" && Array.isArray(xtreamMovies) && xtreamMovies.length > 0) {
      return xtreamMovies;
    }
    // Lazy-loaded slice from IndexedDB. While the fetch is in flight
    // we fall back to the in-memory filter so the user sees content
    // immediately instead of an empty screen.
    if (lazyItems) return lazyItems;
    return typedChannels.filter((c) => {
      if (deferredActiveGroup === "Uncategorized") return !c.group;
      return c.group === deferredActiveGroup;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferredActiveGroup, typedChannels, type, favTick, cueTick, historyItems, channelByUrl, lazyItems, xtreamMovies, sqlMovieTiles]);

  // Reset state when changing tab. Restores the previously-chosen category
  // for THIS type from sessionStorage if available — so when the user
  // navigates Movies → "Action" → Watch → ⬅ Back, they land back in
  // "Action" instead of jumping to the first category.
  useEffect(() => {
    const storageKey = `zapp_active_group_${type}`;
    let saved = null;
    try {
      saved = sessionStorage.getItem(storageKey);
    } catch {
      /* sessionStorage blocked → fall through to defaults */
    }
    // Validate the saved group still exists in the current playlist so a
    // stale value (after a playlist swap) doesn't leave the user staring
    // at "Loading..." with no items.
    const valid =
      saved &&
      (saved === FAV_GROUP ||
        saved === HISTORY_GROUP ||
        saved === ALL_GROUP ||
        groups.some((g) => g.name === saved));

    if (valid) {
      setActiveGroup(saved);
    } else if (type === "movie" || type === "series") {
      // Movies & Series default to the catch-all "All …" group so the
      // user sees every title up-front without first picking a category.
      setActiveGroup(ALL_GROUP);
    } else if (showFavorites && favoritesCount(type) > 0) {
      setActiveGroup(FAV_GROUP);
    } else if (groups.length > 0) {
      setActiveGroup(groups[0].name);
    } else {
      setActiveGroup(null);
    }
    // Stale activeSeries from a previous playlist load can crash the app:
    // its tmdb_id / series_id may now point at something that no longer
    // exists, and downstream useEffects fire fetchSeriesInfo / fetchTmdbTv
    // with mismatched data while the new channels array is settling.
    setActiveSeries(null);
    setActiveSeasonNum(null);
    // Reset the one-shot deep-link guard so `?series=<name>` in the URL
    // can re-hydrate the drill-down after a playlist swap or type change.
    seriesParamUsedRef.current = false;
    // Type-specific default sort — only applied when the user has never
    // picked one for this type. If they did, `readStoredSort` returns
    // their saved choice and we keep it, per user request "when
    // selecting the sort it must stay on what is selected until
    // something else is selected".
    const stored = readStoredSort(type);
    if (stored) {
      setSort(stored);
    } else if (type === "movie") {
      setSort("added_desc");
    } else if (type === "series") {
      setSort("ep_added_desc");
    } else {
      setSort("m3u");
    }
    // v1.4.20 — Depend on `type` and `playlist?.id` only. Previously we
    // also depended on `channels.length` / `groups.length` which meant
    // any background refresh (prewarm finishing, EPG update, user adding
    // a favourite) fired this effect and kicked the user out of their
    // current series drill-down. The deep-link effect can only re-hydrate
    // once per mount, so subsequent resets left the user stranded on
    // the top-level grid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, playlist?.id]);

  // Persist activeGroup so the back-from-stream flow lands the user back
  // in the same category. sessionStorage (cleared on app exit) — the user
  // should not be stuck in "Drama" forever if they relaunch the app.
  //
  // v1.4.65 — Skip writes while we're inside a series drill-down: the
  // drill-down deep-link effect temporarily forces `activeGroup=ALL_GROUP`
  // so it can find the target show across every category, and we don't
  // want that transient value to overwrite the category the user actually
  // picked in the sidebar. `preDrillGroupRef` (below) restores that pick
  // when the user backs out of the drill-down.
  useEffect(() => {
    if (!activeGroup) return;
    if (activeSeries) return;
    try {
      sessionStorage.setItem(`zapp_active_group_${type}`, activeGroup);
    } catch {
      /* sessionStorage blocked → silently skip */
    }
  }, [activeGroup, type, activeSeries]);

  // v1.4.65 — Remember the category the user was in RIGHT BEFORE opening
  // a show, and restore it as soon as they back out of the drill-down.
  // This keeps "picked Drama → opened show → back" landing in Drama
  // instead of the deep-link effect's temporary "All" selection.
  const preDrillGroupRef = useRef(null);
  useEffect(() => {
    if (activeSeries) {
      // Capture only on the null → non-null transition, not on every
      // deep-link forced re-render.
      if (preDrillGroupRef.current === null && activeGroup && activeGroup !== ALL_GROUP) {
        preDrillGroupRef.current = activeGroup;
      }
    } else if (preDrillGroupRef.current) {
      const restore = preDrillGroupRef.current;
      preDrillGroupRef.current = null;
      // Only restore if the current activeGroup is still the transient
      // "All" the deep-link effect set — never overwrite an intentional
      // sidebar click the user might have made in the meantime.
      if (activeGroup === ALL_GROUP && groups.some((g) => g.name === restore)) {
        setActiveGroup(restore);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSeries]);

  // Series view: group episodes by series_name within the active category.
  // When the user picks the Favorites pseudo-category we group across ALL
  // episodes (not just `itemsInGroup`, which is empty for FAV_GROUP) and
  // then filter down to the saved series:// keys.
  const seriesTiles = useMemo(() => {
    if (type !== "series") return [];
    // v1.0.7 — Use the deferred group name so the O(n) episode walk
    // rides the low-priority transition.
    if (deferredActiveGroup === HISTORY_GROUP) return [];
    // v1.3.0 — Prefer the pre-aggregated SQL tiles when available.
    // Skips the ~1s JS Map walk over tens of thousands of episodes on
    // TV-box CPUs. The `series_tiles` table was populated during
    // playlist parse with each tile's poster URL already resolved.
    // Favorites still needs the legacy path (favorite filter happens
    // AFTER aggregation and depends on `getFavorites`).
    if (Array.isArray(sqlSeriesTiles) && deferredActiveGroup !== FAV_GROUP) {
      return sqlSeriesTiles.map((t) => ({
        series_name: t.series_name,
        poster: t.poster_url || null,
        episodes: [],
        firstOrder: t.first_order || 0,
        year: t.year || null,
        last_episode_added_ts: t.last_added_ts || 0,
        series_id: t.series_id || null,
        tmdb_id: t.tmdb_id || null,
        b2cInfo: null,
        group: t.group_name || null,
        __ep_count: t.episode_count || 0,
        __first_ep_url: t.first_ep_url || null,
      }));
    }
    const sourceEpisodes =
      deferredActiveGroup === FAV_GROUP ? typedChannels : itemsInGroup;
    const map = new Map();
    for (const ep of sourceEpisodes) {
      const key = ep.series_name || ep.name;
      if (!map.has(key)) {
        map.set(key, {
          series_name: key,
          poster: ep.logo_url || null,
          episodes: [],
          firstOrder: ep.order ?? 0,
          year: ep.year || null,
          last_episode_added_ts: 0,
          // Xtream series id (numeric) — used by the drill-down view to
          // lazy-fetch full series metadata (plot, cast, director, rating).
          // Captured from the first episode that carries it.
          series_id: ep.xc_kind === "series" && ep.xc_id ? ep.xc_id : null,
          // TMDB id from the bulk listing — lets us short-circuit to
          // TMDB metadata without waiting for `get_series_info`.
          tmdb_id: ep.tmdb_id || null,
          // 1-Stream B2C ships the show-level metadata (cover, plot,
          // cast, director, genre, release_date, rating) inline on
          // every episode row — captured once here so the drill-down
          // can render hero + synopsis on first paint without any
          // extra network calls.
          b2cInfo: ep.b2c_series_info || null,
          group: ep.group || null,
        });
      }
      const entry = map.get(key);
      entry.episodes.push(ep);
      if (!entry.poster && ep.logo_url) entry.poster = ep.logo_url;
      if (!entry.series_id && ep.xc_kind === "series" && ep.xc_id) {
        entry.series_id = ep.xc_id;
      }
      if (!entry.tmdb_id && ep.tmdb_id) entry.tmdb_id = ep.tmdb_id;
      if (!entry.b2cInfo && ep.b2c_series_info) entry.b2cInfo = ep.b2c_series_info;
      const epTs = ep.series_last_modified_ts || ep.added_ts || 0;
      if (epTs > entry.last_episode_added_ts) entry.last_episode_added_ts = epTs;
      if ((ep.year || 0) > (entry.year || 0)) entry.year = ep.year;
    }
    for (const s of map.values()) {
      s.episodes.sort(
        (a, b) =>
          (a.season || 0) - (b.season || 0) || (a.episode || 0) - (b.episode || 0)
      );
    }
    let tiles = Array.from(map.values()).sort((a, b) => a.firstOrder - b.firstOrder);
    if (deferredActiveGroup === FAV_GROUP) {
      // Restrict to favorited series. Match by `series://<id>` first
      // (canonical), then by `series://<name>` (legacy fallback).
      const favs = getFavorites("series");
      const favIds = new Set(favs.map((f) => f.stream_url));
      tiles = tiles.filter((t) => {
        if (favIds.has(`series://${t.series_id || ""}`)) return true;
        if (favIds.has(`series://${t.series_name || ""}`)) return true;
        return false;
      });

      // v1.3.72 — Favorites: bubble shows with new episodes to the
      // top so the user can spot fresh content at a glance. Within
      // the "NEW" group sort by most-recent episode first; within
      // the "no new episodes" group fall back to the existing
      // m3u-order sort.
      tiles.forEach(seedSeriesSeenAtIfMissing);
      tiles.sort((a, b) => {
        const an = hasNewEpisodes(a) ? 1 : 0;
        const bn = hasNewEpisodes(b) ? 1 : 0;
        if (an !== bn) return bn - an; // NEW shows first
        if (an && bn) {
          // both new — most-recently-updated first
          return (b.last_episode_added_ts || 0) - (a.last_episode_added_ts || 0);
        }
        return a.firstOrder - b.firstOrder;
      });
    }
    return tiles;
  // favTick forces a recompute when toggleFavorite mutates localStorage,
  // so the Favorites tile-grid reflects un-favourites without a reload.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsInGroup, typedChannels, type, deferredActiveGroup, favTick, sqlSeriesTiles]);

  // v1.3.72 — deep-link from the global Search screen. If we landed
  // here with `?series=Breaking%20Bad` we want to:
  //   1) Force the activeGroup to __all__ so seriesTiles covers
  //      every category (the show might live in "Drama" but the
  //      search bar doesn't carry category context).
  //   2) As soon as the matching tile is built, jump into the
  //      drill-down view.
  // We use a ref guard so the effect fires exactly once per
  // navigation event from the Search page.
  // Guard so the deep-link `?series=<name>` handler only runs once per
  // mount. Declared at the top of the component; cleared by the
  // type/playlist reset effect so this handler can re-run after a
  // playlist swap.
  useEffect(() => {
    if (!initialSeriesParam) return;
    if (seriesParamUsedRef.current) return;
    if (type !== "series") return;
    // Step 1: switch to All so seriesTiles is comprehensive.
    if (activeGroup !== ALL_GROUP) {
      setActiveGroup(ALL_GROUP);
      return; // wait for the next render with seriesTiles repopulated
    }
    // Step 2: find the tile (case-insensitive name match).
    const wanted = initialSeriesParam.toLowerCase();
    const hit = (seriesTiles || []).find(
      (t) => (t.series_name || "").toLowerCase() === wanted,
    );
    if (hit) {
      setActiveSeries(hit);
      seriesParamUsedRef.current = true;
    } else if ((seriesTiles || []).length > 0) {
      // Tiles loaded but the show isn't in this playlist — mark as
      // used so we don't loop, and just show the All-series grid.
      seriesParamUsedRef.current = true;
    }
  }, [initialSeriesParam, type, activeGroup, seriesTiles]);

  // Final sorted lists
  const sortedItems = useMemo(() => applySort(itemsInGroup, sort), [itemsInGroup, sort]);
  const sortedSeries = useMemo(() => applySortSeries(seriesTiles, sort), [seriesTiles, sort]);

  // Apply the in-category search filter. Done AFTER sort so the visible
  // result order stays consistent. Live TV keeps its own dedicated search
  // page so we only filter for movies/series tabs here.
  const showSearchBar = (type === "movie" || type === "series") && !activeSeries;
  // v1.0.7 — Defer the search query too so typing in the box paints
  // the character immediately while the (potentially O(n)) filter and
  // re-render lag one frame behind. Prevents dropped keystrokes on
  // Smart TV remotes when the tile count is large.
  const deferredQuery = useDeferredValue(searchQuery);
  const q = deferredQuery.trim().toLowerCase();

  // "Recently added" pseudo-category cap. v1.3.25 — was 1000 ("All Movies"
  // / "All Series" used to dump the entire catalog and rendered an OOM-
  // safety banner). User asked for a 20-item spotlight view sorted by
  // recency, regardless of the current sort selector (the sort dropdown
  // still affects every concrete category).
  const ALL_GROUP_RENDER_CAP = 50;
  const isAllGroup = activeGroup === ALL_GROUP;
  const sortedItemsAllOverride = useMemo(() => {
    if (!isAllGroup || type !== "movie") return null;
    return [...itemsInGroup].sort(
      (a, b) => (b.added_ts || 0) - (a.added_ts || 0),
    );
  }, [isAllGroup, type, itemsInGroup]);
  const sortedSeriesAllOverride = useMemo(() => {
    if (!isAllGroup || type !== "series") return null;
    return [...seriesTiles].sort(
      (a, b) => (b.last_episode_added_ts || 0) - (a.last_episode_added_ts || 0),
    );
  }, [isAllGroup, type, seriesTiles]);

  const filteredItems = useMemo(() => {
    const sourceItems = sortedItemsAllOverride || sortedItems;
    const base = q
      ? sourceItems.filter((it) => (it.name || "").toLowerCase().includes(q))
      : sourceItems;
    if (isAllGroup && !q && base.length > ALL_GROUP_RENDER_CAP) {
      return base.slice(0, ALL_GROUP_RENDER_CAP);
    }
    return base;
  }, [sortedItemsAllOverride, sortedItems, q, isAllGroup]);

  const filteredSeries = useMemo(() => {
    const sourceSeries = sortedSeriesAllOverride || sortedSeries;
    const base = q
      ? sourceSeries.filter((s) =>
          (s.series_name || "").toLowerCase().includes(q)
        )
      : sourceSeries;
    if (isAllGroup && !q && base.length > ALL_GROUP_RENDER_CAP) {
      return base.slice(0, ALL_GROUP_RENDER_CAP);
    }
    return base;
  }, [sortedSeriesAllOverride, sortedSeries, q, isAllGroup]);

  // Banner only fires when we're capped AND the underlying catalog is
  // far larger than the cap — keeps the new "Recently added" view clean
  // when a small library would naturally surface ≤20 items anyway.
  const allGroupCapped =
    isAllGroup &&
    !q &&
    (type === "series"
      ? sortedSeries.length > ALL_GROUP_RENDER_CAP
      : sortedItems.length > ALL_GROUP_RENDER_CAP);

  // Memoised so a fresh function reference doesn't break `React.memo` on
  // child tiles. `isTvLayout()` itself is internally cached but we still
  // hoist the result into a stable variable for the entire render.
  const tvLayout = useMemo(() => isTvLayout(), []);

  // v1.4.44 — User-controlled cover tile size. Read once from
  // localStorage; live-update when the Settings page dispatches
  // `tv_player:tileSize`. Applied to every content grid on this page.
  const [tileSize, setTileSizeState] = useState(() => getTileSize());
  useEffect(() => {
    const onChange = (e) => setTileSizeState(e.detail || getTileSize());
    window.addEventListener("tv_player:tileSize", onChange);
    return () => window.removeEventListener("tv_player:tileSize", onChange);
  }, []);
  const gridColsClass = gridClassesFor(tileSize, tvLayout);

  const playItem = useCallback(
    (item, arg2) => {
      // v1.0.9 — Second arg can be either an episode queue (legacy
      // series auto-play) OR an options bag { cue: true, ... }. Detect
      // by shape: arrays are queues, plain objects are opts.
      let episodeQueue = null;
      let opts = {};
      if (Array.isArray(arg2)) {
        episodeQueue = arg2;
      } else if (arg2 && typeof arg2 === "object") {
        opts = arg2;
      }
      // v1.3.94 — pass TMDB metadata (rating / genre / release / poster) to
      // the new custom PlayerOverlay in Watch. `b2cInfo` is the enriched
      // metadata document harvested by the backend Cover & Metadata job;
      // fall back to the tile's own fields for the pre-harvest legacy path.
      const meta = item.b2cInfo || {};
      // v1.4.30 — forward series identity so Watch.jsx can hand it to
      // watchHistory.addToHistory() for Continue Watching aggregation.
      const isSeries =
        (item.content_type || type) === "series" ||
        item.content_type === "episode";
      const showSid = item.series_id || activeSeries?.series_id || null;
      const showName =
        item.series_name || activeSeries?.series_name || (isSeries ? item.name : null);
      navigate("/player/watch", {
        state: {
          stream_url: item.stream_url,
          name: item.name,
          logo: item.logo_url || "",
          type: item.content_type || type,
          episode_queue: Array.isArray(episodeQueue) ? episodeQueue : undefined,
          // v1.0.9 — Movie Cue signal. Watch.jsx reads this and, on
          // playback end, marks the movie watched, removes it from the
          // cue, shows the intermission overlay, then advances.
          cue_mode: !!opts.cue,
          poster: meta.cover || item.cover || item.poster || item.logo_url || "",
          rating: meta.rating ?? item.rating ?? null,
          genre: meta.genre || item.genre || "",
          release_date: meta.release_date || item.release_date || "",
          // v1.4.30 — series identity for Continue Watching.
          series_id: isSeries ? showSid : null,
          series_name: isSeries ? showName : null,
          season: isSeries ? (item.season ?? null) : null,
          episode: isSeries ? (item.episode ?? null) : null,
        },
      });
    },
    [navigate, type, activeSeries],
  );

  // v1.3.1 — Movie tap opens an INLINE detail drill-in (like series).
  // No more MediaInfoModal — no backdrop blur, and metadata always
  // loads via a name-based TMDB fallback so the pane is never empty.
  //
  // Live TV tiles route to the LiveTv LIST view (`/player/live`) with
  // an auto-open state so the preview pane fires immediately —
  // matches user's expectation of "1-click preview, 2-click fullscreen".
  // Catchup tiles play immediately as before.
  const handleTileClick = useCallback(
    (item) => {
      if (type === "movie") {
        startTransition(() => setActiveMovie(item));
      } else if ((item.content_type || type) === "live_tv") {
        // v1.3.1 — Always route Live-TV tile clicks to the list view
        // so the user gets the preview surface + EPG side pane. The
        // old `/player/live/{id}` fullscreen page had no preview and
        // no channel-list — dead-end after tap.
        navigate("/player/live", { state: { autoOpenChannelId: item.id } });
      } else {
        playItem(item);
      }
    },
    [type, navigate, playItem]
  );

  const handleSeriesTileClick = useCallback((s) => {
    // v1.3.72 — record that the user opened this show so the
    // "NEW" pill clears next time the favorites grid renders.
    markSeriesSeen(s);
    // v1.4.30 — Continue Watching pseudo-tile: capture the last-watched
    // episode's stream_url so the drill-down can auto-scroll to it AND
    // (per user pref 4b) simply open the show — the user then picks
    // where to resume from. We don't kick off playback here.
    if (s?.__cw && s.latest?.stream_url) {
      setResumeEpisodeUrl(s.latest.stream_url);
    } else {
      setResumeEpisodeUrl(null);
    }
    // Defer the (heavy) drill-down render so the tile's :active state has
    // a frame to paint first. Without this, the user sees the spinner of
    // doom for a beat on slower TV processors.
    startTransition(() => {
      setActiveSeries(s);
      setActiveSeasonNum(s?.__cw ? (s.latest?.season ?? null) : null);
    });
  }, []);

  const onToggleFav = useCallback(
    (e, item) => {
      e.stopPropagation();
      toggleFavorite(type, { ...item, content_type: type });
      setFavTick((t) => t + 1);
    },
    [type]
  );

  // v1.0.9 — Movie Cue tile-button handler. Mirrors onToggleFav.
  const onToggleMovieCue = useCallback((e, item) => {
    e.stopPropagation();
    toggleMovieCue({ ...item, content_type: "movie" });
    // No local tick needed — the movieCue lib fires a
    // "movie-cue-changed" event that the useEffect at the top of this
    // component picks up and bumps cueTick.
  }, []);

  // ==== v1.4.30 — Long-press / right-click context-menu actions ====
  const openMovieContextMenu = useCallback(
    (item, x, y) => {
      const watched = isWatched(item.stream_url);
      const inCw = activeGroup === HISTORY_GROUP;
      setTileMenu({
        x,
        y,
        title: item.name,
        testId: "tile-context-menu-movie",
        items: [
          watched
            ? {
                key: "unmark",
                label: "Mark as unwatched",
                icon: EyeOff,
                testId: "cm-mark-unwatched",
                onSelect: () => { unmarkWatched(item.stream_url); bumpHistoryTick(); },
              }
            : {
                key: "mark",
                label: "Mark as watched",
                icon: Check,
                testId: "cm-mark-watched",
                onSelect: () => { markWatched(item.stream_url, type || item.content_type); bumpHistoryTick(); },
              },
          inCw
            ? {
                key: "remove-cw",
                label: "Remove from Continue Watching",
                icon: Trash2,
                testId: "cm-remove-cw",
                destructive: true,
                onSelect: () => { removeFromHistory(item.stream_url); bumpHistoryTick(); },
              }
            : null,
        ].filter(Boolean),
      });
    },
    [activeGroup, type, bumpHistoryTick],
  );

  const openSeriesContextMenu = useCallback(
    (series, x, y) => {
      const eps = series.episodes || [];
      const epUrls = eps.map((e) => e.stream_url).filter(Boolean);
      const allWatched = epUrls.length > 0 && epUrls.every((u) => isWatched(u));
      const inCw = series.__cw || activeGroup === HISTORY_GROUP;
      setTileMenu({
        x,
        y,
        title: series.series_name,
        testId: "tile-context-menu-series",
        items: [
          {
            key: "mark-show",
            label: allWatched ? "Mark whole show unwatched" : "Mark whole show watched",
            icon: allWatched ? EyeOff : Check,
            testId: allWatched ? "cm-show-unwatched" : "cm-show-watched",
            disabled: epUrls.length === 0,
            onSelect: () => {
              if (allWatched) {
                unmarkManyWatched(epUrls);
              } else {
                markManyWatched(epUrls, "series");
                removeShowFromHistory({
                  series_id: series.series_id,
                  series_name: series.series_name,
                });
              }
              bumpHistoryTick();
            },
          },
          inCw
            ? {
                key: "remove-cw-show",
                label: "Remove from Continue Watching",
                icon: Trash2,
                testId: "cm-remove-cw-show",
                destructive: true,
                onSelect: () => {
                  removeShowFromHistory({
                    series_id: series.series_id,
                    series_name: series.series_name,
                  });
                  bumpHistoryTick();
                },
              }
            : null,
        ].filter(Boolean),
      });
    },
    [activeGroup, bumpHistoryTick],
  );

  const openSeasonContextMenu = useCallback(
    (seasonNum, seasonEpisodes, x, y) => {
      const epUrls = (seasonEpisodes || []).map((e) => e.stream_url).filter(Boolean);
      const allWatched = epUrls.length > 0 && epUrls.every((u) => isWatched(u));
      setTileMenu({
        x,
        y,
        title: seasonNum > 0 ? `Season ${seasonNum}` : "Other episodes",
        testId: "tile-context-menu-season",
        items: [
          {
            key: "season-watched",
            label: allWatched ? "Mark season unwatched" : "Mark season watched",
            icon: allWatched ? EyeOff : Check,
            testId: allWatched ? "cm-season-unwatched" : "cm-season-watched",
            disabled: epUrls.length === 0,
            onSelect: () => {
              if (allWatched) unmarkManyWatched(epUrls);
              else markManyWatched(epUrls, "series");
              bumpHistoryTick();
            },
          },
        ],
      });
    },
    [bumpHistoryTick],
  );

  const openEpisodeContextMenu = useCallback(
    (ep, x, y) => {
      const watched = isWatched(ep.stream_url);
      setTileMenu({
        x,
        y,
        title: ep.name,
        testId: "tile-context-menu-episode",
        items: [
          watched
            ? {
                key: "ep-unmark",
                label: "Mark episode unwatched",
                icon: EyeOff,
                testId: "cm-ep-unwatched",
                onSelect: () => { unmarkWatched(ep.stream_url); bumpHistoryTick(); },
              }
            : {
                key: "ep-mark",
                label: "Mark episode watched",
                icon: Check,
                testId: "cm-ep-watched",
                onSelect: () => { markWatched(ep.stream_url, "series"); bumpHistoryTick(); },
              },
          {
            key: "ep-remove-cw",
            label: "Remove from Continue Watching",
            icon: Trash2,
            testId: "cm-ep-remove-cw",
            destructive: true,
            onSelect: () => { removeFromHistory(ep.stream_url); bumpHistoryTick(); },
          },
        ],
      });
    },
    [bumpHistoryTick],
  );

  const handleQuickRemoveFromHistory = useCallback(
    (rowKind, itemOrShow) => {
      if (rowKind === "series") {
        removeShowFromHistory({
          series_id: itemOrShow.series_id,
          series_name: itemOrShow.series_name,
        });
      } else {
        removeFromHistory(itemOrShow.stream_url);
      }
      bumpHistoryTick();
    },
    [bumpHistoryTick],
  );
  // Series favourites keyed by a synthetic `series://<id-or-name>` stream_url
  // because the favorites store indexes by stream_url and a series tile is an
  // aggregate of many episodes (no single canonical URL). The key is stable
  // across launches as long as series_id (or the show name) doesn't change.
  const seriesFavKey = useCallback(
    (s) => `series://${s.series_id || s.series_name || ""}`,
    []
  );
  const onToggleSeriesFav = useCallback(
    (e, series) => {
      e.stopPropagation();
      toggleFavorite("series", {
        stream_url: seriesFavKey(series),
        name: series.series_name,
        logo_url: series.poster || null,
        content_type: "series",
        group: series.group || null,
        series_name: series.series_name,
        series_id: series.series_id || null,
      });
      setFavTick((t) => t + 1);
    },
    [seriesFavKey]
  );

  const loadingGroups = status === "loading";

  if (playlists.length === 0 && !(showFavorites && favCount > 0)) {
    return (
      <div className="py-24 text-center text-slate-400">
        <Tv className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p>No playlists assigned to this device.</p>
        <p className="text-xs text-slate-500 mt-1">Ask your provider to assign a playlist.</p>
      </div>
    );
  }

  // ===== v1.3.1 — Movie drill-in view (poster + plot + action buttons) =====
  if (type === "movie" && activeMovie) {
    return (
      <div data-testid={PLAYER.browseScreen}>
        <MovieDetail
          item={activeMovie}
          onBack={() => setActiveMovie(null)}
          onPlay={(it) => {
            setActiveMovie(null);
            playItem(it);
          }}
        />
        <TileContextMenu
          open={!!tileMenu}
          x={tileMenu?.x || 0}
          y={tileMenu?.y || 0}
          items={tileMenu?.items || []}
          title={tileMenu?.title}
          testId={tileMenu?.testId}
          onClose={closeTileMenu}
        />
      </div>
    );
  }

  // ===== Series drill-down view (seasons as tabs + episodes) =====
  if (type === "series" && activeSeries) {
    const bySeason = new Map();
    for (const ep of activeSeriesEpisodes) {
      const s = ep.season ?? 0;
      if (!bySeason.has(s)) bySeason.set(s, []);
      bySeason.get(s).push(ep);
    }
    const seasons = Array.from(bySeason.entries()).sort((a, b) => a[0] - b[0]);
    // Default to the first season available — never `null` so React's tab
    // highlighting + episode list stays in sync.
    const currentSeason =
      activeSeasonNum !== null &&
      seasons.some(([num]) => num === activeSeasonNum)
        ? activeSeasonNum
        : seasons[0]?.[0] ?? null;
    const currentEpisodesRaw =
      seasons.find(([num]) => num === currentSeason)?.[1] || [];
    const currentEpisodes = (() => {
      const list = [...currentEpisodesRaw];
      // Natural numeric sort so "E155" beats "E99" instead of lex-comparing
      // "1" < "9". Critical for episode lists — was the cause of v1.3.32's
      // "Z → A starts at e99" bug.
      const cmp = (a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          numeric: true,
          sensitivity: "base",
        });
      if (seasonEpisodeSort === "az") return list.sort(cmp);
      if (seasonEpisodeSort === "za") return list.sort((a, b) => cmp(b, a));
      // default: by S/E number (already sorted at tile-build time, kept stable)
      return list.sort(
        (a, b) =>
          (a.season || 0) - (b.season || 0) || (a.episode || 0) - (b.episode || 0)
      );
    })();

    // v1.4.66 — D-pad focus flow for the series drill-down.
    // Chromium's default spatial-nav walks visually from the show
    // header straight to the episode list, SKIPPING the season tabs
    // strip AND the "Mark season watched / sort" header row (both are
    // shorter/thinner and lose the geometric race). We take over
    // ArrowUp/Down inside the drill-down and cycle through four
    // logical sections in order:
    //   1) Show header buttons (Back / Play)
    //   2) Season tab strip (active season is the entry point)
    //   3) Header actions row (Mark-season / sort A→Z / Z→A)
    //   4) Episode list rows
    const handleDrillDownKey = (e) => {
      const key = e.key;
      if (
        key !== "ArrowUp" && key !== "ArrowDown" &&
        key !== "ArrowLeft" && key !== "ArrowRight"
      ) return;
      const root = e.currentTarget;
      const focusIn = (selector) => {
        const el = root.querySelector(selector);
        if (!el) return false;
        try { el.focus({ preventScroll: true }); } catch { el.focus(); }
        try { el.scrollIntoView({ block: "nearest" }); } catch { /* */ }
        return true;
      };
      const active = document.activeElement;
      if (!active || !root.contains(active)) return;
      const seasonTab = active.closest('[data-testid^="series-season-tab-"]');
      const headerAction = active.closest(
        '[data-testid="series-season-mark-watched-btn"], [data-testid^="series-episode-sort-"]'
      );
      const episodeRow = active.closest('[data-testid^="series-ep-"]');

      // v1.4.68 — ArrowLeft / ArrowRight on an episode row toggles the
      // watched flag for that episode. Requested by the user because
      // long-press (context menu) sometimes fires playback instead of
      // opening the menu, and there was no reliable D-pad way to
      // mark a single episode watched.
      if (episodeRow && (key === "ArrowLeft" || key === "ArrowRight")) {
        const epId = episodeRow.getAttribute("data-testid").replace(/^series-ep-/, "");
        // Pull the current stream_url out of the row's dataset —
        // EpisodeRow now stores it as `data-stream-url` so we don't
        // have to look the episode up by id.
        const url = episodeRow.getAttribute("data-stream-url") || "";
        if (url) {
          if (isWatched(url)) unmarkWatched(url);
          else markWatched(url, "series");
          bumpHistoryTick();
          e.preventDefault();
          e.stopPropagation();
          void epId;
          return;
        }
      }
      // ArrowLeft/Right elsewhere in the drill-down (season tabs, sort
      // pills, header) uses default browser flow (horizontal siblings).
      if (key === "ArrowLeft" || key === "ArrowRight") return;
      // Anything outside the four "row" containers is treated as the
      // drill-down banner (Back button, show poster, description, play).
      const inHeader = !seasonTab && !headerAction && !episodeRow;
      // Pre-compute selectors for the "next" targets so we can jump.
      const activeTabSel = '[data-testid^="series-season-tab-"][data-active="true"]';
      const firstMarkSel = '[data-testid="series-season-mark-watched-btn"]';
      const firstEpSel = '[data-testid^="series-ep-"]';
      if (key === "ArrowDown") {
        if (inHeader) {
          if (focusIn(activeTabSel)) { e.preventDefault(); e.stopPropagation(); return; }
        }
        if (seasonTab) {
          if (focusIn(firstMarkSel) || focusIn(firstEpSel)) {
            e.preventDefault(); e.stopPropagation(); return;
          }
        }
        if (headerAction) {
          if (focusIn(firstEpSel)) { e.preventDefault(); e.stopPropagation(); return; }
        }
        if (episodeRow) {
          const rows = Array.from(root.querySelectorAll(firstEpSel));
          const idx = rows.indexOf(episodeRow);
          if (idx >= 0 && idx < rows.length - 1) {
            e.preventDefault(); e.stopPropagation();
            try { rows[idx + 1].focus({ preventScroll: true }); } catch { rows[idx + 1].focus(); }
            try { rows[idx + 1].scrollIntoView({ block: "nearest" }); } catch { /* */ }
          }
          return;
        }
      } else if (key === "ArrowUp") {
        if (episodeRow) {
          const rows = Array.from(root.querySelectorAll(firstEpSel));
          const idx = rows.indexOf(episodeRow);
          if (idx > 0) {
            e.preventDefault(); e.stopPropagation();
            try { rows[idx - 1].focus({ preventScroll: true }); } catch { rows[idx - 1].focus(); }
            try { rows[idx - 1].scrollIntoView({ block: "nearest" }); } catch { /* */ }
            return;
          }
          // First row → hop to header-action row (if present), else season tab.
          if (focusIn(firstMarkSel) || focusIn(activeTabSel)) {
            e.preventDefault(); e.stopPropagation();
            return;
          }
        }
        if (headerAction) {
          if (focusIn(activeTabSel)) { e.preventDefault(); e.stopPropagation(); return; }
        }
        if (seasonTab) {
          // From season tab, jump back up to any focusable in the drill-
          // down banner area (Back button is most reliable).
          const banner = root.querySelector('button');
          if (banner) {
            try { banner.focus({ preventScroll: true }); } catch { banner.focus(); }
            try { banner.scrollIntoView({ block: "nearest" }); } catch { /* */ }
            e.preventDefault(); e.stopPropagation();
            return;
          }
        }
      }
    };

    return (
      <div data-testid={PLAYER.browseScreen} onKeyDown={handleDrillDownKey}>
        <button
          data-testid="series-back-to-list-btn"
          onClick={() => {
            // v1.0.8 — Go through the browser's history so the URL /
            // history stack and React state stay in sync. Opening the
            // drill-down pushed a history entry (see the URL-sync
            // effect above), so `history.back()` fires popstate which
            // our popstate handler catches and calls
            // setActiveSeries(null) for us. This prevents the "back
            // press feels like it does nothing / same page over and
            // over" bug the user reported on the TV remote — direct
            // state clearing left an orphan history entry that had
            // to be popped separately.
            try {
              window.history.back();
            } catch {
              // Fallback: if history navigation is unavailable, clear
              // state directly.
              setActiveSeries(null);
              setActiveSeasonNum(null);
            }
          }}
          className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-5 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to {TYPE_LABELS.series}
        </button>

        <div className="flex gap-4 sm:gap-6 mb-6 flex-wrap">
          <div className="w-32 sm:w-44 shrink-0 bg-black rounded-xl overflow-hidden border border-white/5">
            <div className="w-full aspect-[2/3]">
              <PosterImage
                src={seriesInfo?.cover || activeSeries.poster || seriesTmdb?.poster}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <h2 className="text-xl sm:text-2xl font-bold mb-1">{activeSeries.series_name}</h2>
            <div className="text-xs text-slate-400 flex flex-wrap gap-x-3 gap-y-1 mb-2">
              {(seriesInfo?.releaseDate?.slice(0, 4) || activeSeries.year || seriesTmdb?.year) && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {seriesInfo?.releaseDate?.slice(0, 4) || activeSeries.year || seriesTmdb?.year}
                </span>
              )}
              <span className="text-slate-500">
                {seasons.length} season{seasons.length === 1 ? "" : "s"} ·{" "}
                {activeSeriesEpisodes.length} episode
                {activeSeriesEpisodes.length === 1 ? "" : "s"}
                {(loadingB2cEpisodes || loadingXtreamEpisodes) &&
                  activeSeriesEpisodes.length === 0 &&
                  " · loading…"}
              </span>
              {(() => {
                const r = seriesInfo?.rating ?? seriesTmdb?.rating;
                return r != null && Number(r) > 0 ? (
                  <span className="inline-flex items-center gap-1 text-amber-300">
                    <Star className="w-3 h-3 fill-current" />
                    {Number(r).toFixed(1)}
                  </span>
                ) : null;
              })()}
              {(seriesInfo?.genre || seriesTmdb?.genre) && (
                <span className="text-slate-500 truncate max-w-[200px]">
                  {seriesInfo?.genre || seriesTmdb?.genre}
                </span>
              )}
            </div>

            {/* v1.0.6 — Favorite button on the series drill-down. Was
                previously only reachable via long-press on the tile in
                the grid, which broke on Smart TV D-pad (no long-press).
                Now every series detail page exposes a big, D-pad-
                focusable Add / Remove favorites button. */}
            {(() => {
              const fav = isFavorited("series", seriesFavKey(activeSeries));
              // read `favTick` so this button re-renders after a toggle
              void favTick;
              return (
                <button
                  data-testid="series-detail-fav-btn"
                  onClick={(e) => onToggleSeriesFav(e, activeSeries)}
                  className={
                    "inline-flex items-center gap-1.5 text-xs px-3 py-1.5 mt-1 mb-2 rounded-md border transition-colors " +
                    (fav
                      ? "bg-rose-500/15 border-rose-400/40 text-rose-200 hover:bg-rose-500/25 focus:bg-rose-500/25"
                      : "bg-white/5 border-white/10 text-slate-200 hover:bg-white/10 focus:bg-white/10")
                  }
                >
                  <Heart
                    className={"w-3.5 h-3.5 " + (fav ? "fill-current" : "")}
                  />
                  {fav ? "Remove from favorites" : "Add to favorites"}
                </button>
              );
            })()}

            {loadingSeriesInfo && (
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Fetching info from provider…
              </div>
            )}

            {(seriesInfo?.plot || seriesTmdb?.plot) && (
              <p className="text-sm text-slate-300 leading-relaxed max-h-32 overflow-y-auto pr-1 mt-2">
                {seriesInfo?.plot || seriesTmdb?.plot}
              </p>
            )}

            {(seriesInfo?.director || seriesInfo?.cast || seriesTmdb?.director || seriesTmdb?.cast) && (
              <div className="mt-2 space-y-0.5 text-[12px] text-slate-400">
                {(seriesInfo?.director || seriesTmdb?.director) && (
                  <div className="line-clamp-1">
                    <span className="text-slate-500">Director:</span>{" "}
                    <span className="text-slate-300">{seriesInfo?.director || seriesTmdb?.director}</span>
                  </div>
                )}
                {(seriesInfo?.cast || seriesTmdb?.cast) && (
                  <div className="line-clamp-2">
                    <span className="text-slate-500">Cast:</span>{" "}
                    <span className="text-slate-300">{seriesInfo?.cast || seriesTmdb?.cast}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Season tabs — horizontal scroll, one row, no scrolling between seasons */}
        {seasons.length > 0 && (
          <div
            data-testid="series-season-tabs"
            className="flex items-center gap-2 overflow-x-auto pb-2 mb-3 border-b border-white/5 -mx-1 px-1"
          >
            {seasons.map(([num, eps]) => (
              <SeasonTab
                key={num}
                num={num}
                eps={eps}
                active={num === currentSeason}
                onSelect={() => setActiveSeasonNum(num)}
                onContextMenu={(x, y) => openSeasonContextMenu(num, eps, x, y)}
              />
            ))}
          </div>
        )}

        {/* Active season's episodes only — no vertical scroll through every season */}
        {seasons.length > 0 && (
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                {currentEpisodes.length} episode{currentEpisodes.length === 1 ? "" : "s"}
              </div>
              {/* v1.4.65 — Visible "Mark season watched" toggle. Replaces
                  the previous long-press-only context menu (which the
                  user reported often triggered playback because a short
                  tap fires onClick before the 500 ms long-press timer). */}
              {(() => {
                const seasonEpUrls = currentEpisodes.map((e) => e.stream_url).filter(Boolean);
                const allWatched =
                  seasonEpUrls.length > 0 &&
                  seasonEpUrls.every((u) => isWatched(u));
                if (seasonEpUrls.length === 0) return null;
                return (
                  <button
                    type="button"
                    data-testid="series-season-mark-watched-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (allWatched) unmarkManyWatched(seasonEpUrls);
                      else markManyWatched(seasonEpUrls, "series");
                      bumpHistoryTick();
                    }}
                    className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border transition-colors ${
                      allWatched
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                        : "border-white/10 bg-[#11141b] text-slate-200 hover:border-emerald-500/40 hover:text-emerald-200"
                    }`}
                  >
                    {allWatched ? (
                      <>
                        <EyeOff className="w-3.5 h-3.5" />
                        Unmark season
                      </>
                    ) : (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        Mark season watched
                      </>
                    )}
                  </button>
                );
              })()}
            </div>
            <div
              data-testid="series-episode-sort"
              className="inline-flex rounded-md border border-white/10 bg-[#11141b] overflow-hidden text-xs"
            >
              {[
                { v: "se", label: "S/E" },
                { v: "az", label: "A → Z" },
                { v: "za", label: "Z → A" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  data-testid={`series-episode-sort-${opt.v}`}
                  onClick={() => setSeasonEpisodeSort(opt.v)}
                  className={`px-2.5 py-1 transition-colors ${
                    seasonEpisodeSort === opt.v
                      ? "bg-sky-500 text-white"
                      : "text-slate-300 hover:bg-white/5"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          {currentEpisodes.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              No episodes in this season.
            </div>
          ) : (
            currentEpisodes.map((ep, epIdx) => {
              // watchedTick read to force re-render when registry changes.
              void watchedTick;
              // v1.4.64 — pass Continue-Watching progress to each row so
              // partially-watched episodes show a thin blue progress bar
              // even before they cross the "Watched" threshold.
              const epProgress = progressByUrl.get(ep.stream_url) || null;
              // v1.4.68 — pass `watched` as an explicit prop so the
              // memoised EpisodeRow re-renders on ArrowLeft/Right
              // toggles (previously it read isWatched() internally and
              // memo blocked the update).
              const epWatchedNow = isWatched(ep.stream_url);
              return (
                <EpisodeRow
                  key={ep.id}
                  ep={ep}
                  isResume={resumeEpisodeUrl === ep.stream_url}
                  watched={epWatchedNow}
                  progress={epProgress}
                  onPlay={() => {
                    // v1.4.69 — ALWAYS build the follow-up queue (was
                    // previously gated by `isAutoPlayNextEpisodeEnabled`
                    // which meant the "Up Next" countdown on the web
                    // player never fired for users who had auto-play
                    // disabled). The countdown on web/HLS is the
                    // primary "should the next episode start?" UI —
                    // gating it behind another setting made the banner
                    // effectively invisible. Native players still get
                    // the queue for their own auto-advance logic.
                    // Include full episode metadata so the down-stream
                    // Continue Watching aggregation keeps its S/E labels.
                    const queue = currentEpisodes
                      .slice(epIdx + 1)
                      .map((next) => ({
                        stream_url: next.stream_url,
                        name: next.name,
                        logo_url: next.logo_url || null,
                        poster_url: next.poster_url || activeSeries?.poster || null,
                        season: next.season ?? next.season_num ?? null,
                        episode: next.episode ?? next.episode_num ?? null,
                      }));
                    playItem(ep, queue.length > 0 ? queue : undefined);
                  }}
                  onContextMenu={(x, y) => openEpisodeContextMenu(ep, x, y)}
                />
              );
            })
          )}
        </div>
      </div>
    );
  }

  // ===== Default grid view =====
  // v1.4.26 — Force the inline "lg" sidebar layout on Android-TV
  // WebViews regardless of the CSS viewport width. Many TVs report
  // a <1024px CSS width even though the panel is 1920×1080, which
  // used to drop the app into the mobile "Categories" drawer mode
  // instead of showing the permanent left-side category list the
  // user expects. `tvLayout` is derived from Capacitor + UA + screen.width,
  // so it's reliable across every TV target.
  const sidebarInline = tvLayout;
  return (
    <div
      data-testid={PLAYER.browseScreen}
      className={
        sidebarInline
          ? "grid grid-cols-[260px_minmax(0,1fr)] 2xl:grid-cols-[300px_minmax(0,1fr)] gap-6"
          : "lg:grid lg:grid-cols-[260px_minmax(0,1fr)] 2xl:grid-cols-[300px_minmax(0,1fr)] lg:gap-6"
      }
    >
      {/* Mobile-only hamburger row — opens the categories drawer. Stays
          INSIDE the same flex container so the back-arrow row above the
          sidebar isn't duplicated. Hidden on Smart-TV WebViews. */}
      {!sidebarInline && (
        <div className="lg:hidden flex items-center justify-between mb-4 -mt-1">
        <button
          data-testid={PLAYER.btnBack}
          onClick={() => navigate("/player/menu")}
          className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Menu
        </button>
        <h2 className="text-lg font-bold">{TYPE_LABELS[type] || type}</h2>
        <button
          data-testid="player-mobile-categories-btn"
          onClick={() => setSidebarOpen(true)}
          className="flex items-center gap-1.5 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-md px-3 py-1.5"
        >
          <MenuIcon className="w-4 h-4" />
          Categories
        </button>
      </div>
      )}

      {/* Backdrop for the mobile drawer */}
      {sidebarOpen && !sidebarInline && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — inline on lg+ (sticky so it stays visible while the
          movies/series grid scrolls), slide-in drawer on smaller screens.
          On Smart-TV WebViews (`sidebarInline`) we force the inline layout
          regardless of the CSS viewport width so the categories are
          always visible on the left. */}
      <div
        data-testid="player-categories-sidebar"
        className={
          sidebarInline
            ? "static w-auto max-w-none bg-transparent border-r-0 p-0 overflow-y-auto sticky top-2 self-start max-h-[calc(100vh-1rem)]"
            : `fixed lg:static inset-y-0 left-0 z-50 w-[84vw] max-w-[320px] lg:w-auto lg:max-w-none bg-[#07080b] lg:bg-transparent border-r border-white/10 lg:border-r-0 p-4 lg:p-0 overflow-y-auto lg:sticky lg:top-2 lg:self-start lg:max-h-[calc(100vh-1rem)] lg:overflow-y-auto transition-transform duration-200 ${
                sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
              }`
        }
      >
        <button
          data-testid={PLAYER.btnBack}
          onClick={() => navigate("/player/menu")}
          className={
            sidebarInline
              ? "flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-3 transition-colors"
              : "hidden lg:flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-3 transition-colors"
          }
        >
          <ArrowLeft className="w-4 h-4" />
          Menu
        </button>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold">{TYPE_LABELS[type] || type}</h2>
          {!sidebarInline && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1.5 text-slate-400 hover:text-white"
              aria-label="Close categories"
            >
              <XIcon className="w-5 h-5" />
            </button>
          )}
        </div>
        <div
          className={
            sidebarInline
              ? "space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto pr-1"
              : "space-y-1 lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto lg:pr-1"
          }
          onClick={() => !sidebarInline && setSidebarOpen(false)}
          onKeyDown={handleSidebarKey}
        >
          {showFavorites && (
            <button
              data-testid="player-group-favorites"
              onClick={() => setActiveGroup(FAV_GROUP)}
              className={`w-full text-left text-sm px-3 py-2 rounded-md flex items-center justify-between transition-colors mb-1 ${
                activeGroup === FAV_GROUP
                  ? "bg-amber-500/15 text-amber-300 border border-amber-500/40"
                  : "text-amber-200/90 hover:bg-white/5 focus:bg-white/5 border border-transparent"
              }`}
            >
              <span className="flex items-center gap-1.5 truncate">
                <Star
                  className={`w-3.5 h-3.5 ${
                    activeGroup === FAV_GROUP || favCount > 0
                      ? "text-amber-400 fill-amber-400"
                      : "text-slate-600"
                  }`}
                />
                Favorites
              </span>
              <span className="text-[10px] tabular-nums opacity-60 ml-2">{favCount}</span>
            </button>
          )}

          {showHistory && (
            <button
              data-testid="player-group-continue-watching"
              onClick={() => setActiveGroup(HISTORY_GROUP)}
              className={`w-full text-left text-sm px-3 py-2 rounded-md flex items-center justify-between transition-colors mb-1 ${
                activeGroup === HISTORY_GROUP
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                  : "text-emerald-200/90 hover:bg-white/5 focus:bg-white/5 border border-transparent"
              }`}
            >
              <span className="flex items-center gap-1.5 truncate">
                <History
                  className={`w-3.5 h-3.5 ${
                    activeGroup === HISTORY_GROUP || historyCount > 0
                      ? "text-emerald-400"
                      : "text-slate-600"
                  }`}
                />
                Continue Watching
              </span>
              <span className="text-[10px] tabular-nums opacity-60 ml-2">{historyCount}</span>
            </button>
          )}

          {/* v1.0.9 — Movie Cue pseudo-category (Movies tab only). */}
          {type === "movie" && (() => { void cueTick; const n = movieCueCount(); return (
            <button
              data-testid="player-group-movie-cue"
              onClick={() => setActiveGroup(CUE_GROUP)}
              className={`w-full text-left text-sm px-3 py-2 rounded-md flex items-center justify-between transition-colors mb-1 ${
                activeGroup === CUE_GROUP
                  ? "bg-rose-500/15 text-rose-300 border border-rose-500/40"
                  : "text-rose-200/90 hover:bg-white/5 focus:bg-white/5 border border-transparent"
              }`}
            >
              <span className="flex items-center gap-1.5 truncate">
                <ListVideo
                  className={`w-3.5 h-3.5 ${
                    activeGroup === CUE_GROUP || n > 0
                      ? "text-rose-400"
                      : "text-slate-600"
                  }`}
                />
                Movie Cue
              </span>
              <span className="text-[10px] tabular-nums opacity-60 ml-2">{n}</span>
            </button>
          ); })()}

          {showHistory && (
            <button
              data-testid="player-group-all"
              onClick={() => setActiveGroup(ALL_GROUP)}
              className={`w-full text-left text-sm px-3 py-2 rounded-md flex items-center justify-between transition-colors mb-1 ${
                activeGroup === ALL_GROUP
                  ? "bg-sky-500/15 text-sky-300 border border-sky-500/40"
                  : "text-sky-200/90 hover:bg-white/5 border border-transparent"
              }`}
            >
              <span className="flex items-center gap-1.5 truncate">
                <ListOrdered
                  className={`w-3.5 h-3.5 ${
                    activeGroup === ALL_GROUP ? "text-sky-400" : "text-sky-400/70"
                  }`}
                />
                {type === "series" ? "Recently episodes added" : "Recently movies added"}
              </span>
              <span className="text-[10px] tabular-nums opacity-60 ml-2">
                {type === "series"
                  ? Math.min(50, new Set(typedChannels.map((c) => c.series_name || c.name)).size)
                  : Math.min(50, typedChannels.length)}
              </span>
            </button>
          )}

          {loadingGroups ? (
            <div className="text-sm text-slate-500 p-2 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading playlist…
            </div>
          ) : groups.length === 0 ? (
            <div className="text-sm text-slate-500 p-2">No categories.</div>
          ) : (
            groups.map((g) => (
              <button
                key={g.name}
                data-testid={PLAYER.groupItem(g.name)}
                onClick={() => setActiveGroup(g.name)}
                className={`w-full text-left text-sm px-3 py-2 rounded-md flex items-center justify-between transition-colors ${
                  activeGroup === g.name
                    ? "bg-orange-500/15 text-orange-300 border border-orange-500/30"
                    : "text-slate-300 hover:bg-white/5 border border-transparent"
                }`}
              >
                <span className="truncate">{g.name}</span>
                <span className="text-[10px] tabular-nums opacity-60 ml-2">{g.count}</span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Grid */}
      <div
        data-browse-main
        className={"min-w-0" + (isSwitchingGroup ? " opacity-70 transition-opacity" : "")}
        onKeyDown={handleGridKey}
      >
        {/* Search box (Movies / Series only). Sits above the sort toolbar
            so the user can type to filter within the active category. */}
        {showSearchBar && (itemsInGroup.length > 0 || seriesTiles.length > 0) && (
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="browse-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                // v1.4.42 — ArrowDown jumps into the tile grid. Left/Right
                // MUST be left alone (they move the text cursor inside
                // the input); ArrowUp similarly stays in the input.
                if (e.key === "ArrowDown") {
                  const firstTile = document.querySelector(
                    '[data-browse-main] [data-testid^="series-tile-"] button, [data-browse-main] [data-testid^="player-content-"] button'
                  );
                  if (firstTile) {
                    e.preventDefault();
                    try { firstTile.focus({ preventScroll: true }); } catch { firstTile.focus(); }
                    try { firstTile.scrollIntoView({ block: "nearest" }); } catch { /* */ }
                  }
                }
              }}
              placeholder={
                type === "series" ? "Search series in this category…" : "Search movies in this category…"
              }
              className="w-full bg-[#0d1117] border border-white/10 focus:border-orange-400/60 rounded-md text-sm pl-9 pr-9 py-2 text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-orange-400/30"
            />
            {searchQuery && (
              <button
                data-testid="browse-search-clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white p-1"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* v1.0.9 — Movie Cue action bar. Sits above the tile grid when
            the Movie Cue pseudo-category is selected. Play-all launches
            the cinematic queue via the ?cue=1 flag on the first movie's
            watch URL. Clear-all wipes the cue. */}
        {activeGroup === CUE_GROUP && (() => {
          void cueTick;
          const list = getCue();
          const first = list[0];
          return (
            <div
              data-testid="movie-cue-action-bar"
              className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-lg border border-rose-500/30 bg-rose-500/5"
            >
              <div className="flex items-center gap-2 text-sm text-rose-200 mr-auto">
                <ListVideo className="w-4 h-4 text-rose-400" />
                <span>
                  <b className="text-rose-100">{list.length}</b>{" "}
                  {list.length === 1 ? "movie" : "movies"} queued
                </span>
              </div>
              <button
                data-testid="movie-cue-play-all-btn"
                disabled={!first}
                onClick={() => {
                  if (!first) return;
                  playItem(first, { cue: true });
                }}
                className={
                  "inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border transition-colors " +
                  (first
                    ? "bg-rose-500 hover:bg-rose-400 text-white border-rose-500"
                    : "bg-white/5 text-slate-500 border-white/10 cursor-not-allowed")
                }
              >
                <Play className="w-4 h-4 fill-current" />
                Play all in cue
              </button>
              <button
                data-testid="movie-cue-clear-btn"
                disabled={list.length === 0}
                onClick={() => {
                  if (window.confirm(`Remove all ${list.length} movies from the cue?`)) {
                    list.forEach((m) => removeFromCue(m.stream_url));
                  }
                }}
                className={
                  "inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors " +
                  (list.length > 0
                    ? "bg-white/5 border-white/10 text-slate-200 hover:bg-white/10"
                    : "bg-white/5 border-white/10 text-slate-500 cursor-not-allowed")
                }
              >
                Clear all
              </button>
            </div>
          );
        })()}

        {/* Sort toolbar */}
        {/* Sort dropdown. Native `<select>` so it works equally well on
            phones, mouse, and TV remotes (D-pad opens the OS picker).
            The label set is keyed on the active tab — Movies see
            "Recently added", Series see "Recently episodes added", etc. */}
        {(SORT_OPTIONS[type] || []).length > 0 && (itemsInGroup.length > 0 || seriesTiles.length > 0) && (
          <div className="flex items-center gap-2 mb-4">
            <label
              htmlFor="browse-sort-select"
              className="text-[11px] uppercase tracking-wider text-slate-500"
            >
              Sort
            </label>
            <select
              id="browse-sort-select"
              data-testid="browse-sort-select"
              value={sort}
              onChange={(e) => setSortPersisted(e.target.value)}
              className="bg-[#0d1117] border border-white/10 hover:border-white/20 focus:border-orange-400 focus:ring-2 focus:ring-orange-400/30 rounded-md text-xs sm:text-sm text-slate-100 px-2.5 py-1.5 pr-7 outline-none cursor-pointer"
            >
              {(SORT_OPTIONS[type] || []).map(({ v, label }) => (
                <option key={v} value={v} className="bg-[#0d1117]">
                  {label}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-slate-500 ml-1">
              {type === "series"
                ? `${q ? `${filteredSeries.length}/` : ""}${seriesTiles.length} series`
                : `${q ? `${filteredItems.length}/` : ""}${sortedItems.length} items`}
            </div>
          </div>
        )}

        {/* OOM-safety banner — when "All movies"/"All series" exceeds the
            render cap we let the user know there's more behind a category
            pick. Without this they'd think recently-added content was
            missing. */}
        {allGroupCapped && (
          <div
            data-testid="all-group-cap-notice"
            className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-200 flex items-center justify-between gap-3"
          >
            <span>
              Showing the {ALL_GROUP_RENDER_CAP.toLocaleString()} most-recently-added
              of{" "}
              <span className="font-semibold">
                {(type === "series" ? sortedSeries.length : sortedItems.length).toLocaleString()}
              </span>{" "}
              {type === "series" ? "series" : "movies"}. Pick a category to see the rest.
            </span>
          </div>
        )}

        {/* Series tiles vs flat tiles — Favourites under Series uses the
            series-tile grid too (v1.3.24) so users see show posters, not
            individual episode rows. v1.4.30 — Continue Watching for the
            Series tab also uses the SeriesTile grid, populated with the
            aggregated show entries produced by getContinueWatching(). */}
        {type === "series" && activeGroup === HISTORY_GROUP ? (
          historyItems.length === 0 ? (
            <div className="py-24 text-center text-slate-500 text-sm">
              Nothing watched yet — play something and it&apos;ll appear here.
            </div>
          ) : (
            <div className={`grid gap-3 sm:gap-4 ${gridColsClass}`}>
              {historyItems.map((s) => (
                <SeriesTile
                  key={`cw-${s.series_id || s.series_name}`}
                  series={s}
                  onClick={handleSeriesTileClick}
                  fav={isFavorited("series", seriesFavKey(s))}
                  onToggleFav={onToggleSeriesFav}
                  watched={0}
                  total={0}
                  hasNew={false}
                  cwLatest={s.latest || null}
                  onContextMenu={openSeriesContextMenu}
                  onQuickRemove={() => handleQuickRemoveFromHistory("series", s)}
                />
              ))}
            </div>
          )
        ) : type === "series" && activeGroup !== HISTORY_GROUP ? (
          filteredSeries.length === 0 ? (
            <div className="py-24 text-center text-slate-500 text-sm">
              {loadingGroups
                ? ""
                : q
                ? `No series matching "${searchQuery}"`
                : "Pick a category to start."}
            </div>
          ) : filteredSeries.length >= VIRTUAL_THRESHOLD ? (
            <VirtualTileGrid
              // v1.0.2 — Force full unmount+remount on category / type
              // change so Android WebView's GPU compositor releases the
              // old tile bitmaps. Without this the recycled cells ghost
              // through as purple rectangles + outlines when a big
              // category (BBC, Amazon, etc.) is opened.
              key={`series:${activeGroup || "_"}`}
              items={filteredSeries}
              getKey={(s) => s.series_name}
              tvLayout={tvLayout}
              renderTile={(s) => {
                const eps = s.episodes || [];
                let n = 0;
                for (const ep of eps) if (ep?.stream_url && isWatched(ep.stream_url)) n++;
                return (
                  <SeriesTile
                    series={s}
                    onClick={handleSeriesTileClick}
                    fav={isFavorited("series", seriesFavKey(s))}
                    onToggleFav={onToggleSeriesFav}
                    watched={n}
                    total={eps.length}
                    hasNew={activeGroup === FAV_GROUP && hasNewEpisodes(s)}
                    onContextMenu={openSeriesContextMenu}
                  />
                );
              }}
            />
          ) : (
            <div className={`grid gap-3 sm:gap-4 ${gridColsClass}`}>
              {filteredSeries.map((s) => {
                // v1.3.53 — count of episodes the user has watched in
                // this series. Drives the small "✓ N/M" badge on the
                // poster corner. Watched-tick listed as dep so this
                // recomputes when the registry changes.
                /* eslint-disable react-hooks/rules-of-hooks */
                const seriesWatched = (() => {
                  void watchedTick;
                  const eps = s.episodes || [];
                  let n = 0;
                  for (const ep of eps) if (ep?.stream_url && isWatched(ep.stream_url)) n++;
                  return { watched: n, total: eps.length };
                })();
                /* eslint-enable react-hooks/rules-of-hooks */
                return (
                  <SeriesTile
                    key={s.series_name}
                    series={s}
                    onClick={handleSeriesTileClick}
                    fav={isFavorited("series", seriesFavKey(s))}
                    onToggleFav={onToggleSeriesFav}
                    watched={seriesWatched.watched}
                    total={seriesWatched.total}
                    hasNew={activeGroup === FAV_GROUP && hasNewEpisodes(s)}
                    onContextMenu={openSeriesContextMenu}
                  />
                );
              })}
            </div>
          )
        ) : filteredItems.length === 0 ? (
          <div className="py-24 text-center text-slate-500 text-sm">
            {q
              ? `No items matching "${searchQuery}"`
              : activeGroup === FAV_GROUP
              ? "No favorites yet — tap the ★ on any item to add."
              : activeGroup === HISTORY_GROUP
              ? "Nothing watched yet — play something and it'll appear here."
              : activeGroup === CUE_GROUP
              ? "Movie Cue is empty — hover a movie and tap the queue icon to add it."
              : loadingGroups
              ? ""
              : "Pick a category to start."}
          </div>
        ) : filteredItems.length >= VIRTUAL_THRESHOLD ? (
          <VirtualTileGrid
            // v1.0.2 — Force full unmount+remount on category / type
            // change so Android WebView's GPU compositor releases the
            // old tile bitmaps (fixes purple-rectangle / ghost-outline
            // artefacts when switching to a large category).
            key={`${type}:${activeGroup || "_"}`}
            items={filteredItems}
            // v1.4.35 — stream_url first (STABLE across playlist
            // re-imports). Previously we used `id` which the M3U/Xtream
            // merger regenerates on every rewrite, causing React to
            // unmount + remount every tile and drop D-pad focus.
            getKey={(item) => item.stream_url || item.id}
            tvLayout={tvLayout}
            renderTile={(item) => {
              const fav = showFavorites && isFavorited(type, item.stream_url);
              // v1.4.35 — stream_url first (stable across playlist re-imports).
              const itemKey = item.stream_url || item.id;
              const nowProgramme =
                type === "live_tv"
                  ? currentProgramme(epgByChannel, item)
                  : null;
              const watched =
                (type === "movie" || type === "catchup") &&
                isWatched(item.stream_url);
              const inCw = activeGroup === HISTORY_GROUP;
              // v1.4.64 — Show the "Continue Watching" progress bar on
              // ANY tile that carries partial progress, not just tiles
              // rendered under the CW sidebar entry. In CW, the row
              // itself already carries position_s/duration_s; elsewhere
              // we look up the same fields from `progressByUrl`.
              const progress = inCw
                ? { position_s: item.position_s || 0, duration_s: item.duration_s || 0 }
                : (!watched ? progressByUrl.get(item.stream_url) || null : null);
              // v1.0.9 — Movie Cue overlay button for Movies tab.
              const showCueToggle = type === "movie" && !inCw;
              const inMovieCue = showCueToggle && isInCue(item.stream_url);
              return (
                <ContentTile
                  item={item}
                  itemKey={itemKey}
                  type={type}
                  tvLayout={tvLayout}
                  showFavorites={showFavorites}
                  fav={fav}
                  showCueToggle={showCueToggle}
                  inCue={inMovieCue}
                  cueTick={cueTick}
                  onToggleCue={onToggleMovieCue}
                  watched={watched}
                  watchedTick={watchedTick}
                  nowProgramme={nowProgramme}
                  onClick={handleTileClick}
                  onToggleFav={onToggleFav}
                  onContextMenu={
                    type === "live_tv" ? null : openMovieContextMenu
                  }
                  progress={progress}
                  onQuickRemove={inCw ? () => handleQuickRemoveFromHistory("movie", item) : null}
                />
              );
            }}
          />
        ) : (
          <div className={`grid gap-3 sm:gap-4 ${gridColsClass}`}>
            {filteredItems.map((item) => {
              const fav = showFavorites && isFavorited(type, item.stream_url);
              // v1.4.35 — stream_url first (stable across playlist re-imports).
              const itemKey = item.stream_url || item.id;
              // For Live TV channels, surface the currently-airing programme
              // under the channel name. Passes the full item so EPG falls back
              // to name-matching when tvg_id doesn't agree with XMLTV ids.
              const nowProgramme =
                type === "live_tv"
                  ? currentProgramme(epgByChannel, item)
                  : null;
              // v1.3.53 — watched flag for the ✓ overlay (movies / catchup
              // only — live channels never get a "watched" tag).
              const watched =
                (type === "movie" || type === "catchup") &&
                isWatched(item.stream_url);
              const inCw = activeGroup === HISTORY_GROUP;
              // v1.4.64 — see comment above for the same fix on the
              // virtualised grid path.
              const progress = inCw
                ? { position_s: item.position_s || 0, duration_s: item.duration_s || 0 }
                : (!watched ? progressByUrl.get(item.stream_url) || null : null);
              const showCueToggle = type === "movie" && !inCw;
              const inMovieCue = showCueToggle && isInCue(item.stream_url);
              return (
                <ContentTile
                  key={itemKey}
                  item={item}
                  itemKey={itemKey}
                  type={type}
                  tvLayout={tvLayout}
                  showFavorites={showFavorites}
                  fav={fav}
                  showCueToggle={showCueToggle}
                  inCue={inMovieCue}
                  cueTick={cueTick}
                  onToggleCue={onToggleMovieCue}
                  watched={watched}
                  watchedTick={watchedTick}
                  nowProgramme={nowProgramme}
                  onClick={handleTileClick}
                  onToggleFav={onToggleFav}
                  onContextMenu={
                    type === "live_tv" ? null : openMovieContextMenu
                  }
                  progress={progress}
                  onQuickRemove={inCw ? () => handleQuickRemoveFromHistory("movie", item) : null}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* v1.4.30 — Long-press / right-click context menu portal. Single
          instance shared across every tile / row / season tab in Browse. */}
      <TileContextMenu
        open={!!tileMenu}
        x={tileMenu?.x || 0}
        y={tileMenu?.y || 0}
        items={tileMenu?.items || []}
        title={tileMenu?.title}
        testId={tileMenu?.testId}
        onClose={closeTileMenu}
      />
    </div>
  );
}


// ===== Memoised tile components =====
//
// Both tiles are wrapped in React.memo so a single state change in the
// parent <Browse> component (e.g. opening the MediaInfoModal or drilling
// into a series) does NOT trigger a re-render of every other tile in the
// grid. On Smart TVs with weak CPUs this is the difference between an
// instant tap response and a 300-600ms freeze when the user selects a
// movie/series with 500+ siblings in the same category.

const SeriesTile = memo(function SeriesTile({
  series,
  onClick,
  fav,
  onToggleFav,
  watched = 0,
  total = 0,
  hasNew = false,
  cwLatest = null,          // v1.4.30 — Continue Watching latest-episode info
  onContextMenu = null,     // v1.4.30 — long-press / right-click callback
  onQuickRemove = null,     // v1.4.30 — "×" quick-remove button (CW only)
}) {
  const allWatched = total > 0 && watched >= total;
  const lp = useLongPress(
    onContextMenu ? (x, y) => onContextMenu(series, x, y) : null,
    { disabled: !onContextMenu },
  );
  // Continue Watching progress bar %
  const cwPct = (() => {
    if (!cwLatest || !cwLatest.duration_s) return 0;
    return Math.max(0, Math.min(100, Math.round((cwLatest.position_s / cwLatest.duration_s) * 100)));
  })();
  return (
    <div
      data-testid={`series-tile-${series.series_name}`}
      className={`group relative bg-[#11141b] hover:bg-[#171b25] border ${hasNew ? "border-orange-500/70 ring-1 ring-orange-500/30" : "border-white/5 hover:border-sky-500/40"} rounded-xl overflow-hidden focus-within:border-orange-500 focus-within:z-10 focus-within-outline-inset`}
    >
      <button
        onClick={lp.wrapOnClick(() => onClick(series))}
        {...lp.spread}
        className="block w-full text-left"
      >
        <div className="relative aspect-[2/3] bg-black overflow-hidden">
          <PosterImage
            src={series.poster}
            alt={series.series_name}
            tmdbFallback={{ name: series.series_name, year: series.year, kind: "series" }}
            className="w-full h-full object-cover"
          />
          {watched > 0 && (
            <div
              className={`absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold shadow-md ${
                allWatched
                  ? "bg-emerald-500 text-black"
                  : "bg-black/75 text-emerald-300 border border-emerald-400/40"
              }`}
              data-testid={`series-watched-badge-${series.series_name}`}
            >
              <Check className="w-3 h-3" />
              {allWatched ? "Watched" : `${watched}/${total}`}
            </div>
          )}
          {hasNew && (
            <div
              className="absolute top-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide shadow-lg bg-orange-500 text-white animate-pulse"
              data-testid={`series-new-badge-${series.series_name}`}
            >
              <Sparkles className="w-3 h-3" />
              NEW
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/90 to-transparent">
            {cwLatest ? (
              <div className="space-y-1" data-testid={`series-cw-line-${series.series_name}`}>
                <div className="text-[10px] font-semibold text-sky-200 tabular-nums truncate">
                  {cwLatest.season != null ? `S${String(cwLatest.season).padStart(2, "0")}` : ""}
                  {cwLatest.episode != null ? `E${String(cwLatest.episode).padStart(2, "0")}` : ""}
                  {cwPct > 0 ? ` · ${cwPct}%` : ""}
                </div>
                {cwPct > 0 ? (
                  <div className="h-0.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-sky-400"
                      style={{ width: `${cwPct}%` }}
                    />
                  </div>
                ) : null}
              </div>
            ) : (() => {
              const first = series.episodes?.[0];
              const isPlaceholder =
                !first ||
                first.content_type !== "series" ||
                first.xc_kind === "series" ||
                (first.stream_url || "").startsWith("xtream://series/");
              if (isPlaceholder) return null;
              return (
                <span className="text-[10px] font-semibold text-sky-300 tabular-nums">
                  {series.episodes.length} ep{series.episodes.length === 1 ? "" : "s"}
                </span>
              );
            })()}
          </div>
        </div>
        <div className="px-2.5 py-2">
          <div className="text-sm font-medium truncate">{series.series_name}</div>
        </div>
      </button>
      {onQuickRemove ? (
        <button
          data-testid={`cw-quick-remove-series-${series.series_name}`}
          onClick={(e) => { e.stopPropagation(); onQuickRemove(); }}
          aria-label="Remove from Continue Watching"
          title="Remove from Continue Watching"
          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full flex items-center justify-center bg-black/70 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-rose-500 transition-opacity"
        >
          <XIcon className="w-4 h-4" />
        </button>
      ) : onToggleFav ? (
        <button
          data-testid={`fav-toggle-series-${series.series_name}`}
          onClick={(e) => onToggleFav(e, series)}
          aria-pressed={fav}
          title={fav ? "Remove from favorites" : "Add to favorites"}
          className={`absolute top-1.5 right-1.5 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            fav
              ? "bg-amber-400/95 text-black shadow-lg"
              : "bg-black/55 text-white opacity-0 group-hover:opacity-100 hover:bg-amber-400/95 hover:text-black"
          }`}
        >
          <Star className={`w-4 h-4 ${fav ? "fill-current" : ""}`} />
        </button>
      ) : null}
    </div>
  );
});

const ContentTile = memo(function ContentTile({
  item,
  itemKey,
  type,
  tvLayout,
  showFavorites,
  fav,
  // v1.0.9 — Movie Cue overlay button. Only present when
  // `showCueToggle` is true (Movies tab, not Continue Watching).
  showCueToggle = false,
  inCue = false,
  // eslint-disable-next-line no-unused-vars
  cueTick = 0,   // memo cache-buster so re-adds re-render
  onToggleCue,
  watched = false,
  // eslint-disable-next-line no-unused-vars
  watchedTick = 0, // memo cache-buster — referenced so React.memo re-evaluates
  nowProgramme,
  onClick,
  onToggleFav,
  onContextMenu = null,   // v1.4.30 — long-press / right-click callback
  progress = null,        // v1.4.30 — Continue Watching { position_s, duration_s }
  onQuickRemove = null,   // v1.4.30 — "×" quick-remove button (CW only)
}) {
  const lp = useLongPress(
    onContextMenu ? (x, y) => onContextMenu(item, x, y) : null,
    { disabled: !onContextMenu },
  );
  const pct = (() => {
    if (!progress || !progress.duration_s) return 0;
    return Math.max(0, Math.min(100, Math.round((progress.position_s / progress.duration_s) * 100)));
  })();
  return (
    <div
      data-testid={PLAYER.contentItem(itemKey)}
      className="group relative bg-[#11141b] hover:bg-[#171b25] border border-white/5 hover:border-orange-500/40 rounded-xl overflow-hidden focus-within:border-orange-500 focus-within:z-10 focus-within-outline-inset"
    >
      <button
        onClick={lp.wrapOnClick(() => onClick(item))}
        {...lp.spread}
        className="block w-full text-left"
      >
        <div
          className={`relative bg-black overflow-hidden ${
            type === "live_tv"
              ? "aspect-square"
              : tvLayout
              ? "aspect-[3/4]"
              : "aspect-[2/3]"
          }`}
        >
          <PosterImage
            src={item.logo_url}
            alt={item.name}
            tmdbFallback={
              type === "movie" || type === "series"
                ? { name: item.name, year: item.year, kind: type }
                : null
            }
            className={`w-full h-full ${
              type === "live_tv" ? "object-contain p-2" : "object-cover"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-3">
            <Play className="w-6 h-6 text-white drop-shadow-lg" />
          </div>
          {watched && (
            <div
              data-testid={`watched-badge-${itemKey}`}
              className="absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-500 text-black shadow-md"
            >
              <Check className="w-3 h-3" />
              Watched
            </div>
          )}
          {pct > 0 && (
            <div
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10"
              data-testid={`cw-progress-${itemKey}`}
            >
              <div className="h-full bg-sky-400" style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
        <div className="px-2.5 py-2">
          <div className="text-sm font-medium truncate">{item.name}</div>
          {nowProgramme && (
            <div
              className="text-[10px] text-emerald-300/80 truncate mt-0.5 flex items-center gap-1"
              title={nowProgramme.title}
            >
              <span className="inline-block w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
              {nowProgramme.title}
            </div>
          )}
          {!nowProgramme && item.year && (
            <div className="text-[10px] text-slate-500">{item.year}</div>
          )}
        </div>
      </button>
      {onQuickRemove ? (
        <button
          data-testid={`cw-quick-remove-${itemKey}`}
          onClick={(e) => { e.stopPropagation(); onQuickRemove(); }}
          aria-label="Remove from Continue Watching"
          title="Remove from Continue Watching"
          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full flex items-center justify-center bg-black/70 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-rose-500 transition-opacity"
        >
          <XIcon className="w-4 h-4" />
        </button>
      ) : showFavorites ? (
        <button
          data-testid={`fav-toggle-${itemKey}`}
          onClick={(e) => onToggleFav(e, item)}
          aria-pressed={fav}
          title={fav ? "Remove from favorites" : "Add to favorites"}
          className={`absolute top-1.5 right-1.5 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            fav
              ? "bg-amber-400/95 text-black shadow-lg"
              : "bg-black/55 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-amber-400/95 hover:text-black"
          }`}
        >
          <Star className={`w-4 h-4 ${fav ? "fill-current" : ""}`} />
        </button>
      ) : null}
    </div>
  );
});
// v1.4.30 — Small wrapper components so useLongPress can be called at
// hook-safe scope (once per item, not inside a .map()).

const SeasonTab = memo(function SeasonTab({ num, eps, active, onSelect, onContextMenu }) {
  const lp = useLongPress(onContextMenu);
  const label = num > 0 ? `Season ${num}` : "Other";
  return (
    <button
      data-testid={`series-season-tab-${num}`}
      data-active={active ? "true" : "false"}
      onClick={lp.wrapOnClick(onSelect)}
      {...lp.spread}
      className={`shrink-0 text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors border ${
        active
          ? "bg-sky-500 text-white border-sky-500"
          : "text-slate-300 hover:text-white bg-[#11141b] hover:bg-sky-500/10 border-white/5 hover:border-sky-500/40"
      }`}
    >
      <span className="font-semibold">{label}</span>
      <span
        className={`text-[10px] tabular-nums rounded-full px-1.5 py-0.5 ${
          active ? "bg-white/25 text-white" : "bg-white/5 text-slate-400"
        }`}
      >
        {eps.length}
      </span>
    </button>
  );
});

const EpisodeRow = memo(function EpisodeRow({ ep, isResume, watched: watchedProp, onPlay, onContextMenu, progress = null }) {
  const lp = useLongPress(onContextMenu);
  // v1.4.68 — prefer the prop when given; fall back to the registry
  // lookup so this component still works when older callers pass
  // only { ep }.
  const epWatched = watchedProp != null ? watchedProp : isWatched(ep.stream_url);
  const rowRef = useRef(null);
  // v1.4.64 — Progress % for the sky-blue "Continue Watching" bar under
  // the row. Rendered whenever we have a saved position AND the episode
  // hasn't crossed the "Watched" threshold yet.
  const pct = (() => {
    if (epWatched || !progress || !progress.duration_s) return 0;
    return Math.max(0, Math.min(100, Math.round((progress.position_s / progress.duration_s) * 100)));
  })();
  // v1.4.30 — When arriving from Continue Watching, scroll the resume
  // episode into view and briefly focus it so TV-remote users see
  // instantly which episode they're about to pick up.
  useEffect(() => {
    if (isResume && rowRef.current) {
      rowRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
      rowRef.current.focus?.({ preventScroll: true });
    }
  }, [isResume]);
  return (
    <button
      ref={rowRef}
      data-testid={`series-ep-${ep.id}`}
      data-stream-url={ep.stream_url || ""}
      onClick={lp.wrapOnClick(onPlay)}
      {...lp.spread}
      className={`relative w-full group text-left flex items-center gap-3 px-3 py-2.5 rounded-md bg-[#11141b] hover:bg-sky-500/10 border transition-colors ${
        isResume ? "border-sky-400/70 ring-1 ring-sky-400/40" : "border-white/5 hover:border-sky-500/40"
      }`}
    >
      <div className="w-14 text-[11px] tabular-nums text-sky-300/70 font-mono shrink-0">
        {ep.season ? `S${String(ep.season).padStart(2, "0")}` : "—"}
        {ep.episode ? `E${String(ep.episode).padStart(2, "0")}` : ""}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm truncate group-hover:text-sky-200 ${epWatched ? "text-slate-400" : ""}`}>
          {ep.name}
        </div>
        {isResume ? (
          <div className="text-[10px] text-sky-300/80 mt-0.5">Resume where you left off</div>
        ) : null}
      </div>
      {epWatched && (
        <div
          data-testid={`episode-watched-${ep.id}`}
          className="flex items-center gap-1 text-[10px] font-semibold text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 px-1.5 py-0.5 rounded-md shrink-0"
        >
          <Check className="w-3 h-3" />
          Watched
        </div>
      )}
      <div className="w-8 h-8 rounded-full bg-white/5 group-hover:bg-sky-400 text-slate-400 group-hover:text-black flex items-center justify-center shrink-0 transition-colors">
        <Play className="w-3.5 h-3.5 fill-current" />
      </div>
      {pct > 0 && (
        <div
          className="absolute bottom-0 left-2 right-2 h-0.5 bg-white/10 rounded-b"
          data-testid={`episode-progress-${ep.id}`}
        >
          <div className="h-full bg-sky-400 rounded-b" style={{ width: `${pct}%` }} />
        </div>
      )}
    </button>
  );
});
